import {
  compassImageUrl,
  courtImageUrl,
  lightBulbImageUrl,
  lightningImageUrl,
  peopleImageImageUrl,
  shuffleImageUrl,
  TmiType,
} from '@app/app/page-modules/personality-indicator/pi-questions/models/pi-questions.model';

export const tmiTypesReportFr: {
  [trait: string]: TmiType;
} = {
  ENFJ: {
    shortDescription: 'Giver',
    atWorkParagraphs: {
      effectiveness: [
        'Mettre davantage l’accent sur l’accomplissement des tâches',
        'Être plus curieux, sceptique et critique',
        'Être moins loyal envers des personnes ou des causes qui ne le méritent peut-être pas',
        'Agir plus spontanément',
        'Être moins autoritaire envers les personnes qu’ils apprécient, en leur permettant de faire leurs propres erreurs et d’en tirer des leçons',
        'Passer moins de temps sur les questions interpersonnelles',
        'S’appuyer sur leur force pour apporter de la structure et de l’organisation à leur équipe',
        'S’appuyer sur leur force pour parvenir à un consensus grâce à la clarification, la collaboration et la coopération',
        'Se rendre compte que la théorie ne fonctionne pas toujours',
        'Se concentrer sur les aspects positifs plutôt que sur les négatifs',
        'Reconnaître que certaines décisions doivent être prises avec des informations incomplètes',
      ],
      teamLeader: [
        'Énergiser et inspirer l’équipe avec enthousiasme et humour',
        'Adopter un style de leadership égalitaire',
        'Accorder une importance égale au processus et au contenu',
        'Créer une organisation d’équipe claire',
        'Veiller à ce que l’équipe reste sur la bonne voie en utilisant des ordres du jour, des plannings et des suivis de tâches',
        'Écouter tous les membres de l’équipe et prendre en compte chaque opinion',
        'Faciliter la coopération et utiliser efficacement toutes les ressources à la disposition de l’équipe',
        'Diriger leur équipe avec perspicacité et sensibilité',
      ],
      conflicts: [
        'Ils risquent d’être irrités par les membres de l’équipe qui viennent non préparés aux réunions',
        'Peuvent être en conflit avec des personnes qu’ils perçoivent comme peu engagées à résoudre les problèmes de l’équipe',
        'Ont tendance à moins bien s’entendre avec ceux qui manquent de respect pour le temps des autres en arrivant en retard ou en partant trop tôt',
        'Sont plus susceptibles d’entrer en conflit avec des personnes qui ne font pas suffisamment d’efforts pour collaborer',
        'Peuvent avoir une faible tolérance envers ceux qui ne considèrent pas l’impact de leurs décisions sur les autres',
        'Peuvent être dérangés par les personnes qui ne laissent pas suffisamment de place aux échanges sociaux',
      ],
      stressAdvisor: [
        'Devenir de plus en plus critique et trouver des défauts à tout',
        'Être obsédé par le fait d’être apprécié de tout le monde et de gagner leur approbation',
        'Devenir autoritaire avec le besoin d’organiser tout',
      ],
      workEnvironment: [
        'Préfère un environnement animé et interactif, et recherche l’harmonie',
        'Préfère avoir un plan, une routine et s’y tenir',
      ],
      underStress: [],
      underSevereStress: [],
    },
    atWorkTagLine: 'Énergise et inspire l’équipe avec enthousiasme et humour',
    atWorkSneakPeek: 'Une façon d’améliorer leur efficacité au travail est d’être plus curieux, sceptique et critique',
    withOthersTagLine: 'Extraverti, amical et sociable',
    withOthersSneakPeek:
      '%fname pourrait se sentir le plus à l’aise en occupant les rôles suivants dans son équipe : L’Organisateur, Le Générateur d’idées, Le Modérateur',
    withOthersParagraphs: {
      strengths: [
        'Extraverti, amical et sociable',
        'Populaire, sympathique',
        'Expressif',
        'Donne facilement des encouragements et des félicitations',
        'Aime échanger des petites conversations',
        'Style individuel mais garde toujours le groupe à l’esprit',
      ],
      weakSpotsWithOthers: [
        'Hypersensible aux conflits',
        'Peut avoir besoin d’encouragement et de reconnaissance',
        'Peut avoir besoin de temps pour les échanges interpersonnels',
        'Peut percevoir des critiques là où il n’y en a pas',
        'Peu susceptible d’agir spontanément',
      ],
    },
    typeTagLine: 'Capable de trouver des schémas et des significations au-delà des apparences',
    typeSneakPeek: 'Le type de %fname est appelé ENFJ. Quelques mots-clés associés sont : compatissant, harmonieux et catalyseur.',
    typeKeywords: [
      '1. Coopératif',
      '2. Compatissant',
      '3. À la recherche de soi',
      '4. Catalyseur',
      '5. Créateur de relations',
      '6. Possibilités',
      '7. Bienveillant',
      '8. Organisé',
      '9. Soucieux du détail',
      '10. Harmonie',
      '11. Sensible',
    ],
    typePersonalStrengths: [
      'Capable de trouver des schémas et des significations au-delà des apparences',
      'Bon pour faciliter la communication',
      'Créatif, avec une grande imagination',
      'Approche du travail responsable',
      'Bon pour faire des présentations',
      'Bon pour diriger des discussions avec aisance et tact',
      'Réactif aux éloges et aux critiques',
      'Fiable, digne de confiance et loyal',
      'Bon pour prêter attention aux détails et organiser le monde qui les entoure',
      'Désireux de travailler pour le bien commun, coopérateur naturel',
      'Chaleureux, sensible, compréhensif et empathique',
      'Capable de faire avancer les choses',
      'Très attentif aux émotions, besoins et motivations des autres',
      'Désireux de faire ressortir le meilleur de chacun',
      'Capable de s’appuyer sur des concepts théoriques de manière ordonnée et méthodique',
      'Bonne compréhension des concepts théoriques et des principes fondamentaux',
      'Capable d’aborder les problèmes de manière systématique',
    ],
    typeWeakSpots: [
      'Déteste fortement les désaccords et les conflits',
      'Ne trouve pas facile de superviser les autres',
      'Préfère un rôle bien défini au sein de l’équipe',
      'Peut parfois perdre de vue les aspects pratiques',
      'Peut se sentir blessé ou rejeté facilement',
    ],
    description:
      'L’ENFJ est une personne enthousiaste, sociable et réceptive, avec un fort accent sur les relations et l’interaction. Cherchant à atteindre l’harmonie, elle croit profondément en les gens, parfois à l’excès. L’ENFJ est chaleureux et altruiste, plein de charisme, et sait amener les autres à le suivre. Communicateur naturel et à l’aise, il défend ses convictions. Fiable et organisé, il a le désir de réussir sans perdre de vue les besoins des autres.',
    individualTypeByLetters: 'Donneur',
    individualDescription:
      'En tant que Donneur, il est probablement une personne enthousiaste, sociable et réceptive, avec un fort accent sur les relations et l’interaction. Cherchant à atteindre l’harmonie, il a tendance à croire profondément en les gens, parfois à l’excès. Il est chaleureux et altruiste, plein de charisme et capable d’amener les autres à le suivre. Communicateur naturel et à l’aise, il défend ce en quoi il croit. Fiable et organisé, il a le désir de réussir tout en gardant à l’esprit les besoins des autres.',
  },
  ENFP: {
    shortDescription: 'Inspirer',
    atWorkParagraphs: {
      effectiveness: [
        'Ne pas trop personnaliser les décisions',
        'Se rappeler que la mise en œuvre de leurs idées peut être plus complexe et plus longue que leur conception',
        'Être plus pragmatique',
        'Remettre plus souvent en question leurs propres intuitions',
        'S’assurer de ne pas avoir négligé certains faits essentiels',
        'Écouter plus attentivement les membres discrets de leur équipe',
        'Développer leur assurance',
        'Adopter une attitude plus critique envers d’autres points de vue',
        'Prendre le temps de faire une pause et de réfléchir avant de passer à l’action',
        'Essayer de dire « non » plus souvent',
        'Ne pas prendre les critiques personnellement',
        'S’assurer de ne pas promettre plus que ce qu’il est raisonnable ou possible d’accomplir',
        'Veiller à ne pas détourner l’équipe de ses objectifs en étant trop bavard ou en lançant des idées au hasard',
      ],
      teamLeader: [
        'Inspirer les autres par leur enthousiasme',
        'Veiller à ce que chaque voix dans l’équipe soit entendue et comprise. Négocier soigneusement entre les opinions divergentes de leur équipe',
        'Mettre l’accent sur les points d’accord et s’assurer qu’un consensus global soit atteint',
        'Développer des relations personnelles avec chaque membre de l’équipe',
        'Donner aux membres de l’équipe les moyens de partager, reconnaître et valoriser leurs contributions',
        'Recueillir les idées et visions de tous les membres lors de l’élaboration de stratégies pour l’avenir',
        'Présenter des alternatives positives à considérer dans le processus de prise de décision',
        'Chercher à trouver des opportunités de développement pour chaque membre de l’équipe',
      ],
      conflicts: [
        'Peut entrer en conflit avec des membres de l’équipe qu’ils perçoivent comme ayant une vision trop étroite',
        'Peut être irrité par les personnes qui paraissent trop négatives ou constamment pessimistes',
        'Moins susceptible de bien s’entendre avec les personnes qu’ils perçoivent comme trop axées sur les détails',
        'Peut être frustré par les personnes qui n’incluent pas tout le monde dans le processus d’équipe',
        'Susceptible d’être irrité par les personnes qui passent trop de temps à débattre de ce qu’il faut accomplir',
      ],
      stressAdvisor: [
        'Peut avoir du mal à dire « non »',
        'Peut accepter trop de projets sans moyen pratique de les mener à bien',
        'Sous un stress intense, peut avoir du mal à se détacher d’un projet pour répondre à des besoins pratiques ou physiques',
      ],
      workEnvironment: [
        'Un environnement interactif et sociable',
        'Travailler vers quelque chose de significatif en utilisant leur imagination et leur créativité',
      ],
      underStress: [
        'Peut devenir de plus en plus évitant face aux détails et confus face aux faits',
        'Tend à se retirer et à se replier sur soi, devenant négatif et pessimiste',
        'A du mal à se concentrer et à suivre les procédures de routine',
      ],
      underSevereStress: ['Sous un stress intense, a du mal à se détacher d’un projet pour répondre à des besoins pratiques ou physiques'],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek: 'Une façon pour %fname d’améliorer son efficacité au travail est de ne pas trop personnaliser les décisions',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      '%fname pourrait se sentir le plus à l’aise en occupant les rôles suivants dans une équipe : Le Générateur d’idées, Le Modérateur ou Le Relieur de frontières',
    withOthersParagraphs: {
      strengths: [
        'Sociable et extraverti',
        'Facilement apprécié et populaire',
        'Montre un grand intérêt pour les personnes qui l’entourent',
        'Perçu généralement comme une personne chaleureuse, empathique et attentionnée',
        'Évalue le point de vue des autres avec sensibilité et tact',
        'Se fait des amis facilement',
        'Libre d’esprit et ouvert d’esprit',
        'Harmonieux',
        'Conciliant',
        'Accorde une grande importance à l’intégrité',
      ],
      weakSpotsWithOthers: [
        'Peut avoir du mal à affronter les autres',
        'Peut faire des concessions aux dépens de lui-même',
        'Peut devenir rancunier s’il a le sentiment d’avoir été exploité',
        'Peut se décourager si ses idées et opinions ne sont pas valorisées',
        'Les autres peuvent le percevoir comme un peu désorganisé',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Le type de %fname est appelé ENFP. Quelques mots-clés associés sont : enthousiaste, visionnaire ou chaleureux.',
    typeKeywords: [
      '1. Enthousiaste',
      '2. Énergique',
      '3. Solidaire',
      '4. Sociable',
      '5. Visionnaire',
      '6. Axé sur les personnes',
      '7. Créatif',
      '8. Attentionné',
      '9. Chaleureux',
      '10. Compatissant',
      '11. Spontané',
      '12. Flexible',
    ],
    typePersonalStrengths: [
      'Enthousiasme et motive les autres',
      'Voit la vie comme pleine de possibilités',
      'Facilite la communication entre les personnes',
      'Créatif',
      'Développe des solutions nouvelles aux problèmes',
      'Perçoit ce qui importe aux autres',
      'Improvisation',
      'Souvent doué pour l’expression verbale',
      'Capable de voir la vision d’ensemble',
      'Capable de faire des liens rapides entre événements et informations',
      'Pense en métaphores',
      'Naturellement bon pour le brainstorming',
      'Naturellement coopératif et désireux de travailler pour le bien commun',
      'Excellentes compétences relationnelles',
      'Adaptable au changement',
      'Bon pour expliquer et aider les autres à comprendre',
      'Bon pour promouvoir le changement par la création de relations',
      'Idéaliste, regard positif sur la vie',
    ],
    typeWeakSpots: [
      'Hypersensible aux conflits',
      'A besoin de beaucoup d’affirmation de la part des autres',
      'Parfois interprète trop et peut faire des liens trop rapides entre événements et informations',
      'Désire des encouragements et une reconnaissance constants',
      'Enthousiaste au début mais peut manquer de suivi',
      'Peut retarder une décision pour garder les options ouvertes',
      'A du mal à préparer à l’avance',
      'Difficulté à prêter attention aux détails',
      'Intolérant à la routine',
    ],
    description:
      'L’ENFP est une personne sociable, et son enthousiasme fait qu’il se retrouve fréquemment en compagnie des autres. On le décrit souvent comme l’âme de la fête. L’ENFP est curieux et indépendant, aime le changement et la flexibilité mais peut perdre facilement sa concentration. Il a beaucoup de « grandes idées » et cherche à passer à l’action. Il souhaite impliquer tout le monde dans ses idées et plans pour stimuler la créativité et l’innovation. Il se soucie profondément des autres et de ce que les gens pensent, et est capable d’illuminer la vie de ceux qui l’entourent.',
    individualTypeByLetters: 'Inspirer',
    individualDescription:
      'En tant qu’Inspirer, il est probablement une personne sociable, et son enthousiasme fait qu’il se retrouve fréquemment en compagnie des autres. On le décrit souvent comme l’âme de la fête. Curieux et indépendant, il aime le changement et la flexibilité mais peut perdre facilement sa concentration. Il a probablement beaucoup de « grandes idées » et cherche à passer à l’action, voulant impliquer tout le monde dans ses idées et plans pour stimuler la créativité et l’innovation. Il se soucie probablement profondément des autres et de ce que les gens pensent, et est capable d’illuminer la vie de ceux qui l’entourent.',
  },
  ENTJ: {
    shortDescription: 'Exécutif',
    atWorkParagraphs: {
      effectiveness: [
        'Faire preuve de plus de patience avec les personnes qui pourraient avoir besoin de plus de temps pour comprendre des idées complexes et abstraites',
        'Développer une tolérance plus grande envers l’incompétence ou l’inefficacité perçue des autres',
        'Être moins critique et accepter davantage les forces et faiblesses des autres',
        'Ne pas fixer de standards irréalisables',
        'Reconnaître le besoin des autres en soutien émotionnel',
        'Trouver des manières constructives de critiquer',
        'Développer un respect sain pour les détails',
        'Essayer d’écouter davantage et de parler moins',
        'Accorder plus de temps à la réflexion personnelle',
      ],
      teamLeader: [
        'Assumer un rôle de coordination',
        'Améliorer la compréhension et l’achèvement d’une tâche en fournissant des modèles',
        'Communiquer leur vision de manière à motiver les autres à participer activement',
        'Être plus objectif pour orienter les efforts et activités des autres vers un objectif global',
        'Fournir un sens clair des objectifs globaux',
        'Être ouvert d’esprit et donc valoriser les contributions de toutes sources',
        'Être démocratique et encourager la participation',
        'Assumer la responsabilité de leurs décisions',
        'Être totalement dédié à la tâche',
        'Motiver les autres en fixant des standards élevés',
      ],
      teamLeaderFlipSide: [
        'Peut parfois être perçu comme peu compétitif, pas assez assertif ou peu ambitieux',
        'Peut parfois être perçu comme réservé ou distant',
        'Ne pas prendre le temps de rassembler des informations ou d’explorer d’autres alternatives',
        'Peut paraître trop contrôlant',
      ],
      conflicts: [
        'Peut être dérangé par les personnes qu’il perçoit comme incompétentes',
        'Peut être frustré par les personnes montrant un manque d’engagement envers les objectifs de l’équipe',
        'Plus susceptible d’être gêné par les personnes adoptant une attitude désinvolte et paraissant trop désorganisées',
        'Plus susceptible de trouver difficile de travailler avec des personnes qui continuent à discuter d’un sujet même après que la clarté ait été atteinte',
        'Peut être irrité par les personnes qui s’attachent à discuter de détails qu’il perçoit comme non pertinents pour atteindre l’objectif',
        'Tendance à entrer en conflit avec des personnes qui manquent de perspective systématique et d’organisation dans l’usage du temps et des ressources',
      ],
      stressAdvisor: [],
      workEnvironment: ['Structuré et organisé', 'Orienté vers l’avenir', 'Orienté vers les objectifs'],
      underStress: [
        'Peut devenir hypercritique envers lui-même et les autres',
        'Peut se mettre en colère et devenir autoritaire sans trop considérer l’impact de son comportement sur les autres',
      ],
      underSevereStress: [
        'Peut se sentir piégé et trop émotif',
        'Peut percevoir des commentaires innocents comme des attaques personnelles',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      'Une façon pour %fname d’améliorer son efficacité au travail est d’être moins critique et d’accepter davantage les forces et faiblesses des autres',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      '%fname pourrait se sentir le plus à l’aise en occupant les rôles suivants dans son équipe : L’Organisateur, Le Générateur d’idées, Le Critique',
    withOthersParagraphs: {
      strengths: [
        'Sociable et extraverti',
        'La logique et l’analyse sont primordiales',
        'Critique et rapide à questionner',
        'Prêt à se challenger et à challenger les autres',
        'Assertif, décisif',
        'Communique directement',
        'Aime organiser les gens et prendre les commandes',
        'Fiable et digne de confiance',
        'Accorde de l’importance à la justice',
      ],
      weakSpotsWithOthers: [
        'Les personnes moins assertives peuvent le percevoir comme dominant, opiniâtre ou dogmatique',
        'Peut sembler se tenir en trop haute estime',
        'Certaines personnes peuvent en vouloir à sa tendance à prendre en charge les situations',
        'Peut paraître froid et impersonnel',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Le type de %fname est appelé ENTJ. Quelques mots-clés associés sont : planificateur, franc ou enthousiaste',
    typeKeywords: [
      '1. Planificateur',
      '2. Visionnaire',
      '3. Organisateur',
      '4. Logique',
      '5. Analytique',
      '6. Théoricien',
      '7. Non-conformiste',
      '8. Argumentatif',
      '9. Franc et chaleureux',
      '10. Décisif',
      '11. Objectif',
      '12. Enthousiaste',
    ],
    typePersonalStrengths: [
      'Orienté vers les objectifs, organisé et capable de livrer les résultats prévus',
      'Adopte une vision à long terme pour résoudre les problèmes en se concentrant sur des idées liées à la vision d’ensemble',
      'Identifier les procédures et politiques illogiques et inefficaces',
      'Analyser et apporter un ordre logique aux problèmes',
      'Développer de nouvelles théories/modèles/idées/systèmes pour résoudre les problèmes',
      'Critiquer objectivement les solutions potentielles',
      'Mettre en œuvre des solutions',
      'Apporter de l’énergie à une tâche',
      'Apprendre de nouvelles choses, élargir ses connaissances et les transmettre aux autres',
      'Présenter des idées',
      'Donner des conseils d’expert',
      'Se concentrer sur ce qui pourrait être, pas sur ce qui est',
      'Conceptualisation et pensée abstraite',
      'Prendre des décisions',
      'Appliquer les connaissances',
    ],
    typeWeakSpots: [
      'Peut parfois paraître plus positif et confiant que ce que son expérience dans un domaine justifie',
      'Peut parfois sembler dominant et autoritaire',
      'Peut parfois pousser trop fort pour accomplir le travail',
      'Peut irriter les autres en voulant que tout le travail soit terminé immédiatement',
    ],
    description:
      'L’ENTJ est une personne honnête et décisive. Il est axé sur les résultats et organisé, avec une vision d’ensemble et une planification stratégique à long terme. Les ENTJ sont souvent très charismatiques et capables d’amener les gens à les suivre. Ils choisissent d’être directs et déterminés dans leurs interactions, évitant de trop s’impliquer ou de devenir trop personnels. L’ENTJ cherche à être efficace et est peu susceptible d’abandonner une tâche, s’efforçant de surmonter chaque obstacle sur son chemin.',
    individualTypeByLetters: 'Exécutif',
    individualDescription:
      'En tant qu’Exécutif, il est probablement une personne honnête et décisive. Il a tendance à être particulièrement axé sur les résultats et organisé, avec une vision globale et une planification stratégique à long terme. En tant qu’Exécutif, il est probablement charismatique et capable d’amener les gens à le suivre ; choisissant d’être direct et déterminé dans ses interactions, tout en évitant de trop s’impliquer ou de devenir trop personnel. Il cherche probablement à être efficace et est peu susceptible d’abandonner une tâche, surmontant tous les obstacles sur son chemin.',
  },
  ENTP: {
    shortDescription: 'Visionnaire',
    atWorkParagraphs: {
      effectiveness: [
        'Contribuer par leur ingéniosité sans submerger les autres avec trop de possibilités',
        'Ne pas disperser leur énergie sur trop de projets en même temps',
        'Développer leur autodiscipline',
        "Utiliser leurs compétences analytiques pour trouver la direction qu'ils veulent suivre",
        'Travailler sur leurs compétences en prise de décision',
        "Se rappeler que mettre en œuvre une idée est plus compliqué que de l'imaginer",
        "Travailler sur le suivi en gardant des notes de ce qu'ils ont promis",
        'Permettre aux autres de briller et de prendre la scène plus souvent',
        'Ne pas réduire tout à un modèle conceptuel',
        "Prêter attention aux informations qui ne font pas partie d'un domaine logique",
        'Prioriser les possibilités alternatives, potentiellement avec un accent plus fort sur les personnes',
        "Accepter qu'ils ne peuvent pas tout savoir",
        'Améliorer les techniques de gestion du temps',
        'Tenir davantage compte des besoins interpersonnels des collègues',
        'Se concentrer davantage sur les aspects pratiques',
      ],
      teamLeader: [
        'Être visionnaire, générer de nouvelles idées facilement',
        'Motiver les autres en fixant des standards élevés',
        "S'attendre à ce que les autres se surpassent et innovent en allant au-delà des tâches requises",
        'Impliquer et inspirer les meilleures personnes à travailler avec eux',
        "Intégrer les intérêts des membres de l'équipe dans un plan d'action",
        'Permettre une interaction dynamique entre processus et contenu',
        'Questionner et critiquer diverses possibilités',
        'Offrir différentes perspectives ainsi que des informations pour trouver la meilleure solution la plus objective',
      ],
      conflicts: [
        "Susceptible d'être irrité par les personnes qui refusent de considérer de nouvelles possibilités",
        'Peut être dérangé par les personnes qui ne comprennent pas que le processus et le contenu sont également importants',
        "Plus susceptible d'être irrité par les personnes incapables de distinguer l'essentiel du trivial",
        "Peut avoir des conflits avec des personnes manquant d'orientation vers la réussite et d'attitude proactive",
      ],
      stressAdvisor: [
        'Se lance dans trop de projets qui ne peuvent pas être terminés',
        'Évite les détails et devient agité et frénétique',
        "Finira par se retirer d'une situation et aura besoin de beaucoup de soutien",
      ],
      workEnvironment: [
        'Implique moins de répétition et de routine, avec de nombreuses opportunités de converser et de collaborer avec les autres',
        'Implique imagination et innovation où les idées sont débattues',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      "Une façon pour %fname d'améliorer son efficacité au travail est de ne pas disperser son énergie sur trop de projets en même temps",
    withOthersTagLine: '---',
    withOthersSneakPeek:
      "%fname pourrait se sentir le plus à l'aise en occupant les rôles suivants dans son équipe : Le Générateur d'idées, Le Créateur ou Le Critique",
    withOthersParagraphs: {
      strengths: [
        'Sociable et grégaire',
        'Compagnie stimulante, alerte et directe',
        'Questionne et challenge rapidement',
        'Dégage beaucoup d’énergie et d’humour',
        'Enthousiasme contagieux',
        'Spontané',
        'Compatissant et amical',
        "Peut argumenter pour le plaisir de débattre sur n'importe quel côté d'une question",
        'Valorise les autres pour leur intelligence et leurs compétences, plutôt que pour leur statut',
        'Capable de voir différents points de vue',
        'Bon pour lire les autres',
        'Flexible, adaptable et ouvert d’esprit',
        'Décontracté et facile à vivre',
        'Non-conformiste, individualiste',
      ],
      weakSpotsWithOthers: [
        'Peut parfois paraître insensible aux questions émotionnelles',
        'Peut rebuter les autres par sa préférence pour le débat et le combat intellectuel',
        'Grand besoin d’autonomie',
        'Peut submerger les autres par des bouffées d’idées',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek:
      'Le type de %fname est appelé ENTP. Quelques mots-clés associés sont : visionnaire, entrepreneur ou orienté vers la réussite',
    typeKeywords: [
      '1. Logique',
      '2. Ingénieux',
      '3. Libre penseur',
      '4. Enthousiaste',
      '5. Visionnaire',
      '6. Possibilités',
      '7. Personne axée sur la vision d’ensemble',
      '8. Entrepreneurial',
      '9. Imaginatif',
      '10. Adaptable',
      '11. Compétent',
      '12. Orienté vers la réussite',
    ],
    typePersonalStrengths: [
      'Rapide, logique, ingénieux et compétent dans de nombreux domaines',
      "Une mentalité du type « S'ils peuvent l'imaginer, ils peuvent le faire »",
      'Aime résoudre des problèmes complexes et les aborde comme des défis',
      'Excellent pour percevoir les motifs et voir la vision d’ensemble',
      "Excellent pour découvrir les principes sous-jacents d'un problème",
      'Bon pour résoudre les problèmes avec des solutions à long terme',
      "Imagine les possibilités d'une situation puis les analyse stratégiquement pour déterminer quelle action serait la plus efficace à entreprendre",
      "Souvent habile à remarquer les tendances et à prévoir l'avenir",
      'Forte orientation vers la réussite, aime se challenger ainsi que les autres',
      'Bon pour motiver les autres en fixant des standards élevés',
      "Capable d'improviser et de répondre aux besoins du moment",
      'Guidé par l’inspiration',
      'Conceptualisateur naturel',
      "A tendance à travailler par rafales d'énergie et d'enthousiasme",
      'Peut être un visionnaire dynamique, un architecte du futur',
      'Bon pour prendre en charge les efforts de changement',
      'Forte curiosité intellectuelle',
      'Bon pour faire des observations objectives',
      'Ne croit pas aux règles et naviguera autour pour atteindre son objectif',
      'Autonome et indépendant de l’autorité',
      'Capable de travailler indépendamment',
      'Bon pour appliquer les connaissances et utiliser ses compétences',
      'Innovateur, planificateur et concepteur, qui apprécie la complexité',
      'Forte capacité à développer de nouvelles théories, modèles, idées ou systèmes',
      'Maîtrise des mots',
      'Bon pour analyser les choses avec logique et empathie',
      'Penseur original',
      'Capable de bien argumenter un point de vue',
      'Optimiste éternel',
      'Bon pour former et clarifier des idées avec de nouvelles perspectives créatives',
      'Bon pour fournir analyse et synthèse',
    ],
    typeWeakSpots: [
      'Impatient avec une faible tolérance lorsque les choses avancent trop lentement',
      'A tendance à prendre trop de choses en même temps',
      "S'ennuie avec la routine et les travaux peu stimulants, et fera rarement la même chose de la même manière",
      'Accorde une grande valeur à la compétence et à la réussite, en attendant trop des autres',
      'Probablement tendance à se fixer des standards élevés qui peuvent mener à des situations de doute de soi',
    ],
    description:
      "L'ENTP est un pionnier du changement et de la débrouillardise. Ayant besoin de flexibilité et d'innovation, ce sont des individus sociables qui aiment débattre des idées et concepts, jouant souvent l'avocat du diable pour amener les autres à penser à de nouvelles façons diverses de faire les choses. Énergique et bavard, l'ENTP recherche de nouveaux environnements à explorer pour trouver de nouveaux défis intellectuels. Ils n'aiment pas la monotonie. La répétition les amène à se disperser ou à faire les choses différemment afin d'éviter l'ennui et d'injecter variété et excitation dans une tâche.",
    individualTypeByLetters: 'Visionnaire',
    individualDescription:
      "En tant que Visionnaire, il est probablement un pionnier du changement et de la débrouillardise. Il prospère grâce à la flexibilité et cherche l'innovation, il est sociable et aime débattre des idées et concepts. Il peut jouer l'avocat du diable pour amener les autres à penser à de nouvelles façons diverses de faire les choses. Énergique et bavard, il est probable qu'il recherche de nouveaux environnements à explorer pour trouver de nouveaux défis intellectuels. La peur de la monotonie et de la répétition le pousse à se disperser, et il essaie également de faire les choses différemment pour éviter l'ennui et injecter variété et excitation dans une tâche.",
  },
  ESFJ: {
    shortDescription: 'Pourvoyeur',
    atWorkParagraphs: {
      effectiveness: [
        "Se rappeler d'être plus concis et aller droit au but",
        'Apprendre à ne pas prendre les critiques ou le rejet personnellement, mais à reconnaître les faits objectifs sous-jacents',
        'Agir moins rapidement au nom des autres',
        'Laisser les autres faire leurs propres erreurs',
        "Se rappeler de ne pas parler d'un problème jusqu'à l'épuiser, laisser passer les détails sans s'y attarder",
        "Mettre moins l'accent sur le consensus",
        "Accepter que parfois il soit nécessaire de réévaluer les décisions convenues à la lumière d'informations supplémentaires, même si cela signifie recommencer",
        'Accepter que les plans et les processus ne sont pas gravés dans le marbre',
        'Être plus réceptif aux intuitions des autres',
        "Accepter que l'ambiguïté fasse partie de la vie et que certaines décisions doivent être prises dans l'incertitude",
      ],
      teamLeader: [
        'Définir un ordre du jour clair et parvenir au consensus et à la conclusion en respectant les engagements temporels',
        "Créer une atmosphère où chacun comprend son importance au sein de l'équipe et se sent encouragé à contribuer",
        'Travailler en coopération pour déterminer ce qui doit être fait et qui est le mieux placé pour accomplir ces tâches',
        'Motiver les autres en utilisant des arguments persuasifs qui prennent en compte les sentiments des personnes',
        "Se concentrer sur des moyens pratiques pour rassembler l'équipe dans la résolution de problèmes",
        "Clarifier les points ouverts en posant les bonnes questions plutôt qu'en donnant toutes les réponses eux-mêmes",
      ],
      conflicts: [
        "Peut rencontrer des conflits avec des personnes qu'ils perçoivent comme passives",
        "Plus susceptible d'avoir des conflits avec des personnes qu'ils considèrent comme ne prenant aucune responsabilité pour résoudre les problèmes",
        "Peut être irrité par des personnes qui refusent de respecter l'ordre du jour et font perdre du temps",
        "Peut être dérangé par des personnes qui perturbent le consensus en jouant l'avocat du diable",
        'Probablement faible tolérance envers les personnes qui ralentissent le progrès en interrompant constamment le flux',
        "Plus susceptible d'avoir des conflits avec des personnes qui ne complètent pas les tâches correctement et à temps",
      ],
      stressAdvisor: [],
      workEnvironment: [],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek: "Une façon d'améliorer leur efficacité au travail est de laisser les autres faire leurs propres erreurs",
    withOthersTagLine: '---',
    withOthersSneakPeek:
      "Ils pourraient se sentir le plus à l'aise en prenant les rôles suivants dans leur équipe : L'Organisateur, Le Créateur, Le Précis",
    withOthersParagraphs: {
      strengths: [
        'Chaleureux, amical et soutenant',
        'Bavard, populaire',
        'Attentionné',
        'Harmonieux',
        'Tendance à avoir un bon sens des responsabilités sociales et altruiste',
        'Respectueux, bien élevé',
        'Caractère sympathique qui prend des décisions basées sur les valeurs',
        'Réfléchi',
        'Fort sens du devoir',
        'Loyal envers amis et collègues',
        'Accorde de l’importance aux traditions',
      ],
      weakSpotsWithOthers: [
        'Tendance à prendre les critiques objectives personnellement',
        'Déteste profondément les discordes, évite les conflits',
        "Recherche particulièrement l'appréciation",
        "Peut être trop rigide, ne s'adapte pas facilement au changement",
        "Imagine des affronts là où il n'y en a pas",
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: '%fname est de type ESFJ. Quelques mots-clés associés : Harmonique, Sympathique ou Ordonné',
    typeKeywords: [
      '1. Harmonie',
      '2. Chaleureux',
      '3. Ordonné',
      '4. Coopératif',
      '5. Attentionné',
      '6. Détaillé',
      '7. Sympathique',
      '8. Dévoué',
      '9. Terre-à-terre',
      '10. Systématique et organisé',
      '11. Planifie',
    ],
    typePersonalStrengths: [
      'Attitude positive et optimiste',
      'Apporte du réconfort',
      'Bon pour apaiser les conflits',
      'Loyal aux systèmes établis et bon pour préserver les traditions',
      'Prend le travail au sérieux et croit que les autres devraient faire de même',
      'Consciencieux et excellente attention aux détails',
      "Apporte une aura de chaleur à tout ce qu'ils font",
      "Très susceptible de suivre les choses jusqu'au bout même pour les petites affaires",
      'Attentif, ils remarquent ce dont les autres ont besoin dans leur vie quotidienne et essaient de le fournir',
      "Excellent joueur d'équipe, coopératif",
      'Bon pour créer un environnement harmonieux',
      'Bon pour organiser le monde qui les entoure',
      'Capable de terminer les tâches à temps et selon les normes requises',
      'Vision réaliste et factuelle',
      'Bon pour définir des modes de travail orientés vers les objectifs communs',
      "Approche responsable du travail, capable de trouver un moyen d'être utile",
      'Bon pour planifier dans le détail',
      'Bon pour offrir une perspective systématique et pratique',
      'Bon pour organiser et motiver les gens à contribuer',
      'Bon pour être inclusif et reconnaître que les gens ont des idées différentes',
      "Apporte de l'harmonie à l'équipe grâce à une organisation soignée, de l'enthousiasme et de l'humour",
      'Fort intérêt pour les choses qui affectent directement et visiblement la vie des gens',
      "Forte orientation vers le service client, aime faire sentir aux gens qu'ils sont valorisés",
    ],
    typeWeakSpots: [
      "Forte envie d'harmonie, tendance à éviter les conflits autant que possible",
      "Travaille mieux avec encouragement et éloges, mais pourrait se retenir s'ils ne les reçoivent pas",
      "Peu d'intérêt pour les concepts abstraits, lorsque les implications pour les gens ne sont pas immédiates",
      "A besoin d'un environnement de travail ordonné",
      "Tendance à fortement désapprouver le changement, l'instabilité et les crises",
      "Soutient l'ordre existant et pourrait être trop dépendant de l'autorité. Prend trop pour acquis l'ordre établi et les règles",
      "Désir d'appartenir à un groupe. Peut devenir désenchanté et démotivé s'il n'est pas apprécié pour qui il est et ce qu'il contribue",
      'Peut surévaluer son statut et son ancienneté par rapport à la compétence technique',
      "A besoin d'un rôle d'équipe clairement défini et d'objectifs clairement définis",
      "Concentration sur les détails pouvant être interprétée par certains collègues comme un manque d'imagination",
    ],
    description:
      "L'ESFJ est un individu compatissant, axé sur le soutien aux autres et la création d'harmonie. L'ESFJ est consciencieux et orienté vers les relations, voyant le positif chez les gens. Extrêmement loyal, avec un engagement envers son équipe, il se concentre sur l'exécution et est prêt à aider les autres. L'ESFJ aime maintenir l'harmonie au sein de l'équipe, sans compromettre l'atteinte des objectifs, en recherchant des solutions agréables pour tous.",
    individualTypeByLetters: 'Fournisseur',
    individualDescription:
      "En tant que Fournisseur, il est probable qu'ils soient des individus compatissants, axés sur le soutien aux autres et la création d'harmonie. Ils sont susceptibles d'être consciencieux et orientés vers les relations, voyant le positif chez les gens. Ils tendent à être extrêmement loyaux, avec un engagement envers leur équipe, se concentrant sur l'exécution et prêts à aider les autres. En tant que Fournisseur, ils sont susceptibles de maintenir l'harmonie au sein de l'équipe, sans compromettre l'atteinte des objectifs, en recherchant des solutions agréables pour tous.",
  },
  ESFP: {
    shortDescription: 'Interprète',
    atWorkParagraphs: {
      effectiveness: [
        "S'éloigner de leur focalisation sur le présent, pour adopter une approche plus axée sur les tâches",
        'Développer une meilleure appréciation de la pensée globale',
        'Trouver le juste équilibre entre vivacité et légèreté',
        'Exprimer leurs propres points de vue de manière plus assertive',
        "Mettre l'accent sur les considérations liées aux tâches",
        "Être plus assertif et dire 'non' aux demandes déraisonnables",
        'Se concentrer sur les exigences détaillées des tâches',
        'Ne pas prendre les critiques personnellement',
        "Reconnaître la valeur de l'analyse logique",
      ],
      teamLeader: [
        "Essayer davantage de lancer l'ensemble du processus et de résumer les décisions prises",
        "Encourager la collaboration entre les membres de l'équipe et veiller à ce que les forces et contributions de chacun soient appréciées",
        "Encourager une large participation et s'assurer que toutes les opinions et suggestions ont été prises en compte avant de prendre une décision",
        'Encourager les autres pour obtenir des résultats positifs',
      ],
      conflicts: [
        "Peut être frustré par les personnes qu'ils perçoivent comme trop négatives, critiques, accusatrices ou pessimistes",
        'Peut être dérangé par les personnes qui parlent de manière très érudite sans aller droit au but',
        "Peut être irrité par les personnes qui se concentrent uniquement sur la tâche et deviennent insensibles aux personnes autour d'elles",
        "Tendance à avoir des difficultés à travailler avec des personnes unilatérales qui n'apprécient pas les points de vue et contributions différents",
      ],
      stressAdvisor: [],
      workEnvironment: ['Pratique', 'Manuel', 'Dynamique et amusant', 'Où il y a liberté et espace', 'Non routinier'],
      underSevereStress: [
        "Ils pourraient être perçus comme trop conciliants, trop personnalisés et ne pas accorder suffisamment d'attention aux détails, à la théorie ou à la vue d'ensemble",
      ],
      weakSpotsWithOthers: [
        "Sous un stress intense, ils pourraient avoir du mal à se détacher d'un projet pour répondre à des besoins pratiques ou physiques",
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      "Une façon d'améliorer leur efficacité au travail est de développer une meilleure appréciation de la pensée 'vue d'ensemble'",
    withOthersTagLine: '---',
    withOthersSneakPeek:
      "Ils pourraient se sentir le plus à l'aise en occupant les rôles suivants dans leur équipe : Le Créateur, Le Modérateur ou Le Relieur de Frontières",
    withOthersParagraphs: {
      strengths: [
        'Extraverti',
        'Chaleureux',
        'Amical',
        'Soutenant',
        "Aime s'amuser",
        'Spontané',
        'Tolérant',
        "Ouvert d'esprit",
        'Vif',
        'Bavard',
        'Compatissant',
        'Tactful',
        'Coopératif',
        'Égalitaire',
        'Facile à vivre',
        'Sensible aux besoins des autres',
      ],
      weakSpotsWithOthers: [
        'Parfois trop conciliant',
        'Parfois trop affectueux',
        'Peut être perçu comme un peu désorganisé',
        'Peut prendre les critiques trop personnellement',
        'Peut sur-interpréter une situation',
        'Peut essayer de faire trop de choses en même temps',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Le type de %fname est appelé ESFP. Quelques mots-clés associés sont énergique, expérientiel ou réaliste',
    typeKeywords: [
      '1. Enthousiaste',
      '2. Optimiste',
      "3. Orienté vers l'action",
      '4. Énergique',
      '5. Pragmatique',
      '6. Tolérant',
      '7. Émotionnel',
      '8. Réaliste',
      '9. Terre-à-terre',
      '10. Nouvelles activités, nouvelles personnes',
      '11. Expérientiel',
      '12. Adaptable',
    ],
    typePersonalStrengths: [
      "Un niveau élevé d'énergie",
      'Trouver des solutions pratiques à des problèmes concrets et réels',
      'Développer des solutions en tenant compte des besoins et sentiments des autres',
      'Empathique et compréhensif envers les gens, prêt à considérer le point de vue des autres',
      "Non attaché aux approches anciennes, prêt à s'adapter",
      "Orienté vers les objectifs, prêt à suivre le chemin direct vers le succès plutôt que de perdre du temps et de l'énergie inutilement",
      'Prêt et enthousiaste à faire avancer les choses',
      "Aime l'action et faire bouger les choses",
      'Sait ce qui se passe et participe avec enthousiasme',
      'Apporte du bon sens et une approche réaliste à la résolution de problèmes',
      'Bon pour mémoriser les faits',
      'Bon pour répondre aux crises',
      'Bon pour apaiser les conflits',
      'Bon pour être inclusif',
      'Energise les autres par une pensée positive',
    ],
    typeWeakSpots: [
      'Peut être considéré comme ayant une approche trop personnelle',
      'Peut sembler ne pas prendre les choses suffisamment au sérieux',
      'Peut irriter les autres en rejetant les débats abstraits et conceptuels',
      'Tendance à éviter les conflits',
      'Tendance à ne pas aimer la routine et à trouver difficile de suivre les procédures et systèmes établis',
      "Probablement pas assez de temps consacré à la maîtrise des théories, préférant passer directement à l'action",
      "Une attention continue aux détails n'est pas forcément naturelle pour eux",
    ],
    description:
      "L'ESFP a tendance à apprécier les choses esthétiquement plaisantes. Ils sont susceptibles d'être engageants et optimistes, et d'encourager les autres, étant particulièrement sensibles à leurs besoins. Amicaux et pratiques, la coopération et l'harmonie sont importantes pour l'ESFP, qui aime les activités pratiques et préfère l'action et l'animation pour rester intéressé et engagé. Apportant espoir et optimisme, et une attitude de 'je peux le faire', l'ESFP est susceptible d'apporter une atmosphère coopérative et personnelle à une situation, avec but et action.",
    individualTypeByLetters: 'Interprète',
    individualDescription:
      "En tant qu'Interprète, ils ont tendance à apprécier les choses esthétiquement plaisantes. Ils sont susceptibles d'être engageants et optimistes, et d'encourager les autres, étant particulièrement sensibles à leurs besoins. Amicaux et pratiques, la coopération et l'harmonie sont importantes pour eux, car ils aiment les activités pratiques et préfèrent l'action et l'animation pour rester intéressés et engagés. Apportant espoir et optimisme, et une attitude de 'je peux le faire', ils sont susceptibles d'apporter une atmosphère coopérative et personnelle à une situation, avec but et action.",
  },
  ESTJ: {
    shortDescription: 'Gardien',
    atWorkParagraphs: {
      effectiveness: [
        'Devenir plus sensible aux besoins des personnes',
        "Regarder au-delà des faits vers la vue d'ensemble",
        'Écouter davantage les autres et accorder plus de poids aux opinions opposées',
        "S'assurer d'avoir recueilli suffisamment d'informations avant de se précipiter pour terminer",
        'Déléguer davantage et permettre aux autres de développer leurs compétences en leadership',
        "Comprendre que le processus est crucial pour livrer un bon produit. Apprendre à avancer plus vite qu'une étape à la fois",
        "Être plus ouvert à des points de vue alternatifs même s'ils semblent peu sensés au départ",
        "Mettre l'accent sur les considérations personnelles ainsi que sur les tâches",
        "Permettre aux autres suffisamment d'espace pour être innovants et promouvoir la créativité",
        'Développer leur sens de la diplomatie et du tact',
        "Être plus ouvert à l'expérimentation",
        'Suspendre le jugement critique lors du brainstorming',
        "Augmenter leur tolérance à l'incertitude",
      ],
      teamLeader: [
        'Être bon pour fournir des orientations',
        'Être ferme dans la mise en œuvre des plans',
        'Être bon pour gérer le comportement avec des principes stricts',
        'Élaborer des plans détaillés et orientés résultats',
        'Être bon pour définir et clarifier les problèmes',
        "Attribuer à chaque membre de l'équipe une tâche et une responsabilité bien définies",
        "Constamment challenger leur équipe pour donner le meilleur d'elle-même",
        "S'assurer que les gens respectent les règles et les politiques",
        'Montrer une énergie et un engagement élevés',
        'Être bon pour accomplir les tâches',
      ],
      conflicts: [
        "Plus susceptible d'être irrité par les membres de l'équipe qui, selon eux, manquent de concentration, s'éloignent de la tâche et perdent du temps",
        "Peut être irrité par les membres de l'équipe qui sont peu préparés et ne suivent pas les tâches",
        "Plus susceptible d'être frustré par les réunions qu'ils perçoivent comme manquant d'objectifs clairs, d'agenda ou d'organisation",
        "Peut être insatisfait lorsqu'il travaille dans un rôle manquant de définition et d'objectifs clairs",
        "Peut entrer en conflit avec les membres de l'équipe souvent en retard, absents ou présents mais non participatifs",
      ],
      underStress: [
        'Sous stress : Peut devenir hypercritique envers lui-même et les autres.',
        'Peut se sentir non apprécié ou interpréter des commentaires innocents comme des attaques personnelles.',
        'Sous grand stress : Les ESTJ peuvent également devenir en colère et autoritaires, donnant des directives sans réfléchir aux conséquences pour les autres',
      ],
      stressAdvisor: [
        'Sous stress : Peut devenir hypercritique envers lui-même et les autres.',
        'Peut se sentir non apprécié ou interpréter des commentaires innocents comme des attaques personnelles.',
        'Sous grand stress : Les ESTJ peuvent également devenir en colère et autoritaires, donnant des directives sans réfléchir aux conséquences pour les autres.',
      ],
      workEnvironment: [
        'Permettant la concentration sur les détails et les plans, où les responsabilités sont bien définies',
        'Où les gens sont fréquemment présents, avec des opportunités de prendre les commandes',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek: "Une façon d'améliorer leur efficacité au travail est de devenir plus sensible aux besoins interpersonnels des gens.",
    withOthersTagLine: '---',
    withOthersSneakPeek:
      "%fname pourrait se sentir le plus à l'aise en occupant les rôles suivants dans son équipe : Le Créateur, L'Organisateur ou Le Précisionniste",
    withOthersParagraphs: {
      strengths: [
        'Sociable, vivant et extraverti',
        'Confiant et sûr de lui',
        'Rapide à exprimer ses propres opinions',
        'Franc-parler, direct, sans fioritures',
        'Responsable et digne de confiance',
        "Valorise l'efficacité et la performance chez les autres",
        'Critique ouvertement ceux qui ne répondent pas à ses propres standards',
        "Prêt à superviser les autres pour voir l'ordre naturel et pratique des choses se mettre en place",
        'Proactif dans la création de nouveaux contacts sociaux',
        'Déterminé',
        "À l'aise pour donner des directives",
      ],
      weakSpotsWithOthers: [
        'Comprendre les sentiments des autres peut ne pas être naturel',
        'Peut être perçu comme trop direct',
        'Peut sembler trop contrôlant. Tendance à prendre des décisions pour les autres sans leur consentement',
        "Un désir trop fort de certitude peut rendre difficile l'acceptation de perspectives et observations différentes",
        'Peut être considéré par les autres comme autocratique',
        "Tendance à prendre des décisions trop rapidement sans recueillir suffisamment d'informations",
        'Peut sembler prendre des décisions juste pour conclure les choses',
        'Peut paraître inflexible',
        "Peut négliger le facteur humain dans l'accomplissement des tâches",
        'Peut être perçu comme trop critique et impatient',
        'Peut ne pas être suffisamment inclusif',
        "La focalisation sur les détails et la praticité peut donner l'impression d'un manque d'imagination",
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Le type de %fname est appelé ESTJ. Quelques mots-clés associés sont décisif, logique ou gestionnaire',
    typeKeywords: [
      '1. Décisif',
      '2. Déterminé',
      '3. Ordonné',
      '4. Précis',
      '5. Responsable',
      '6. Procédures',
      '7. Logique',
      '8. Gestionnaire',
      '9. Organisé',
      '10. Basé sur les faits',
      '11. Analytique',
      '12. Principiel',
    ],
    typePersonalStrengths: [
      "Bon pour analyser et apporter de l'ordre dans le monde",
      'Bon pour organiser des projets et des personnes',
      'Consciencieux et travaille avec énergie pour accomplir les tâches',
      "Forte concentration sur l'obtention de résultats immédiats et visibles de la manière la plus efficace possible",
      'Approche pragmatique, réaliste et pratique',
      'Gère les problèmes et les autres de manière directe et sans fioritures',
      'Fort esprit logique',
      'Forte éthique de travail',
      "Bon pour s'occuper des détails de routine",
      'Fort en planification',
      'Décisif et rapide dans la mise en œuvre des décisions',
      'Bon pour documenter les procédures et les informations',
      'Excellente attention aux détails et bon pour détecter et corriger les erreurs',
      'Bon pour améliorer les choses par petites étapes',
      'Aime prendre les commandes',
      'Bon gardien des traditions, loyal au système et aux structures existantes',
      'Fiable, prend ses responsabilités au sérieux',
      'Bon pour compiler des informations',
      'Bon pour appliquer les règles et politiques',
      'Vision réaliste, avec un accent clair sur ce qui doit être fait ensuite',
      "Dispose généralement d'un ensemble clair de standards logiques et les suit systématiquement",
      'Bon pour encourager la prudence',
      'Approche systématique et méthodique',
    ],
    typeWeakSpots: [
      "Faible tolérance à l'incertitude, préfère être sûr de ses convictions même si elles sont erronées plutôt que de faire face à l'incertitude",
      "Réticent à changer les structures et traditions existantes. Peut valoriser la stabilité plutôt que l'innovation",
      "Peut être trop concentré sur l'immédiat et négliger les conséquences à long terme",
      'Inflexible face aux changements de plan',
      "Forte concentration sur les détails pouvant limiter la capacité à voir la vue d'ensemble",
      'Peut étouffer sa propre créativité et celle des autres en rejetant les idées sans utilité immédiate',
      "Peut surévaluer l'importance de la hiérarchie, du pouvoir formel et de l'autorité",
      'Peut oublier de prendre en compte les sentiments et points de vue des autres lors de la prise de décision',
      'Parfois trop prudent',
    ],
    description:
      "L'ESTJ est susceptible d'être énergique et axé sur l'exécution. Bien qu'il ne soit pas nécessairement très flexible, l'ESTJ préfère généralement la planification et le but à atteindre, et est heureux de prendre en charge une situation. Les ESTJ s'appuient sur des méthodes éprouvées, en travaillant avec des informations factuelles et des processus systématiques. En tant que type extraverti, ils aiment être au centre de l'attention, débattant de leurs idées et concepts. Travaillant mieux avec des directives et un plan à suivre, les ESTJ sont capables de créer de l'ordre à partir du chaos.",
    individualTypeByLetters: 'Gardien',
    individualDescription:
      "En tant que Gardien, ils sont susceptibles d'être énergique et axé sur l'exécution. Bien qu'ils ne soient pas nécessairement très flexibles, ils préfèrent la planification et le but à atteindre, et sont généralement heureux de prendre en charge une situation. En tant que Gardien, ils s'appuient sur des méthodes éprouvées, travaillant avec des informations factuelles et des processus systématiques. En tant que type extraverti, ils sont susceptibles d'être heureux d'être au centre de l'attention, débattant de leurs idées et concepts. Travaillant mieux avec des directives et un plan à suivre, ils sont susceptibles de créer de l'ordre à partir du chaos.",
  },
  ESTP: {
    shortDescription: 'Fonceur',
    atWorkParagraphs: {
      effectiveness: [
        "S'appuyer sur leur force à présenter les faits et à adopter une vue réaliste",
        "S'appuyer moins sur l'improvisation (même s'ils y excellent) et plus sur la préparation",
        "Prendre plus de temps avant d'agir",
        "Utiliser leur bon sens de l'humour, tout en étant conscient de l'impact négatif que des remarques sarcastiques peuvent avoir sur les autres",
        "Chercher à éviter une crise, même s'ils aiment secrètement l'adrénaline qu'elle procure",
        "Être prêt à faire des compromis lorsque cela est approprié pour faire avancer l'équipe",
        "Pratiquer leurs compétences d'écoute",
        "Accepter et encourager l'innovation chez les autres",
        "Prendre davantage en compte les besoins interpersonnels d'un collègue",
      ],
      teamLeader: [
        'Donner à leur équipe une direction et un objectif clairs',
        'Articuler le problème, recueillir les avis, discuter des alternatives et prendre une décision pratique qui fonctionne',
        'Faire travailler leur équipe ensemble de la manière la plus efficace',
        "Motiver leurs collaborateurs à passer à l'action",
        "S'adapter rapidement aux changements de l'environnement et renforcer ou modifier leur stratégie",
        "Être impartial envers les suggestions des autres tant qu'elles sont logiques",
      ],
      conflicts: [
        "Peut être irrité par les membres de l'équipe qu'ils perçoivent comme passifs et peu engagés",
        "Peut avoir une faible tolérance envers les personnes qu'ils considèrent comme distraites ou irréalistes",
        "Plus susceptible d'avoir des problèmes avec les membres de l'équipe qui ne voient pas le chemin logique et se laissent distraire par des objectifs secondaires sans gain immédiat",
        "Plus susceptible d'être irrité par les personnes qui se plaignent trop",
        "Peut être irrité par ceux qui manquent de sens de l'humour",
      ],
      stressAdvisor: [
        'Résister de plus en plus aux règles et devenir compétitif',
        'Se lancer dans trop de projets et devenir spontané voire imprudent',
        'Réfléchir avant de parler, sinon ils risquent de paraître brusques et indifférents',
      ],
      workEnvironment: [
        "Préférant les opportunités de flexibilité et d'idées",
        "N'aimant pas être contraint par trop de règles et régulations",
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek: "Une façon pour %fname d'améliorer son efficacité au travail est de passer à l'action plus progressivement",
    withOthersTagLine: '---',
    withOthersSneakPeek:
      "%fname pourrait se sentir le plus à l'aise en occupant les rôles suivants dans son équipe : Le Connecteur, Le Créateur ou Le Modérateur",
    withOthersParagraphs: {
      strengths: [
        'Sociable et grégaire',
        'Spontané et enthousiaste',
        "Ouvert d'esprit et tolérant",
        "Bon sens de l'humour",
        'Apprécié pour son réalisme terre-à-terre',
        'Enthousiaste',
        'Aventurier et aimant le plaisir',
        'Assertif',
      ],
      weakSpotsWithOthers: [
        'Peut blesser les autres involontairement en exprimant ses opinions directement',
        'Souvent trop prompt à abandonner les systèmes qui offrent stabilité aux autres',
        "Peut être impatient avec les dimensions interpersonnelles d'une tâche",
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Le type de %fname est appelé ESTP. Quelques mots-clés associés sont logique, axé sur les faits ou adaptable',
    typeKeywords: [
      '1. Spontané',
      "2. Ouvert d'esprit",
      '3. Orienté action',
      '4. Logique',
      '5. Pratique',
      '6. Improvisateur',
      '7. Dépanneur',
      '8. Aventurier',
      '9. Énergique',
      '10. Humoristique',
      '11. Adaptable',
      '12. Basé sur les faits',
    ],
    typePersonalStrengths: [
      'Bon pour résoudre des problèmes pratiques sur le moment',
      'Dépanneur naturel et bon pour éliminer les obstacles ou résoudre les difficultés',
      'Attitude positive et détermination enthousiaste à réussir',
      'Bon dans les situations de crise',
      'Adaptable, tolérant et flexible',
      "Fortement orienté action et capable d'enthousiasmer les autres à passer à l'action",
      'Réaliste et terre-à-terre',
      "Bon dans l'analyse et la prise de décisions logiques",
      'Bon avec les choses concrètes pouvant être manipulées, démontées ou assemblées',
      'Rapide à nouer de nouvelles relations',
      'Prêt à contourner les systèmes et protocoles établis à la recherche de solutions',
    ],
    typeWeakSpots: [
      'Tendance à ne pas aimer les longues explications théoriques',
      "S'ennuie avec trop de stabilité",
      "Tendance à se précipiter trop vite dans l'action",
      "Peut négliger les exigences détaillées d'une tâche",
      "Préfère apprendre par l'expérience pratique plutôt que par l'étude ou la réflexion individuelle",
    ],
    description:
      "L'ESTP a probablement un fort goût pour l'action et aime un environnement rapide. Ils sont susceptibles d'être enthousiastes, charmants et directs. L'ESTP se concentre davantage sur les activités pratiques et la planification. Bien qu'ils apprécient la variété, les défis et la flexibilité que cela apporte, ils ont du mal à gérer des tâches banales et répétitives. L'ESTP doit se méfier de vouloir faire trop de choses à la fois et de prendre trop de risques, ce qui peut créer le chaos. Ils privilégient la rationalité et la réflexion avant les sentiments des autres, préférant traiter des détails tangibles et spécifiques.",
    individualTypeByLetters: 'Fonceur',
    individualDescription:
      "En tant que Fonceur, ils ont probablement un fort goût pour l'action et apprécient un environnement rapide. Ils sont susceptibles d'être enthousiastes, charmants et directs. Ils se concentrent davantage sur les activités pratiques et la planification, appréciant la variété, les défis et la flexibilité que cela apporte, mais ayant du mal à gérer des tâches banales et répétitives. En tant que Fonceur, ils doivent se méfier de vouloir faire trop de choses à la fois et de prendre trop de risques, ce qui peut créer le chaos. Ils privilégient la rationalité et la réflexion avant les sentiments des autres, préférant traiter des détails tangibles et spécifiques.",
  },
  INFJ: {
    shortDescription: 'Protecteur',
    atWorkParagraphs: {
      effectiveness: [
        'Exprimer plus librement leurs pensées, sentiments et valeurs personnelles',
        "Ne pas s'accrocher trop obstinément à une idée",
        'Développer leurs compétences en réseautage et nouer des amitiés informelles',
        "Reconnaître l'importance du bavardage léger",
        'Veiller à ne pas ignorer le présent en étant trop concentré sur les possibilités futures',
        'Apprendre à mieux se vendre',
        "Pratiquer des techniques d'affirmation de soi",
        "Se rappeler que les gens sont plus susceptibles de mettre en œuvre une idée lorsqu'on leur donne l'occasion de contribuer",
        "Reconnaître qu'il est important de ne pas se retirer des autres en période de stress",
        'Ne pas fixer des standards irréalisables pour soi-même et pour les autres',
      ],
      teamLeader: [
        "Voir la vue d'ensemble et développer une vision",
        "Gérer les membres de l'équipe individuellement et attendre que chaque membre accomplisse les tâches assignées",
        "Stimuler l'équipe avec des idées créatives et intuitives",
        'Faire des compromis avec la majorité, sauf en cas de conflit avec leurs valeurs personnelles',
        'Gérer avec succès les drames interpersonnels au sein des groupes',
        'Évaluer la dynamique du groupe et fournir des résumés et analyses approfondies sur la marche à suivre',
        'Clore efficacement les réunions',
      ],
      conflicts: [
        "Peut être frustré par des personnes perçues comme hostiles, impatientes et pessimistes ou qui n'apprécient pas les autres",
        "Peut avoir des problèmes avec des personnes qui donnent des conseils prématurés et hors sujet, et qui refusent d'examiner les problèmes en profondeur",
        'Peut être irrité par des personnes très bavardes',
        'Peut être gêné par des personnes qui ne contribuent pas au travail en cours',
        'Peut entrer en conflit avec des personnes dont le seul focus est le contenu et qui ne prêtent aucune attention au processus',
      ],
      stressAdvisor: [
        'Peut devenir excessivement coopératif, ressentant le besoin de plaire à chaque occasion',
        'Trouve difficile de gérer les critiques',
        'Ressent le besoin de résoudre les problèmes par soi-même',
      ],
      workEnvironment: [
        "Où il y a des opportunités de travailler sur la vue d'ensemble, d'être créatif, avec la possibilité de flexibilité",
        'Où il y a moins de tâches et de plannings routiniers',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek: "Une façon d'améliorer votre efficacité au travail est de ne pas s'accrocher trop obstinément à des idées",
    withOthersTagLine: '---',
    withOthersSneakPeek:
      "%fname pourrait se sentir le plus à l'aise en occupant les rôles suivants dans une équipe : Le Précis, Le Générateur d'idées, Le Modérateur",
    withOthersParagraphs: {
      strengths: [
        'Calme, amical et sociable',
        'Accorde une grande importance aux personnes et aux relations dans sa vie',
        'Compatissant',
        'Doucement persuasif',
        'Profond émotionnellement',
        'Peu enclin à se confier aux autres avant de bien les connaître',
        'Sensible aux besoins des autres',
        "Apprécie l'harmonie entre les personnes",
        'Attentionné et prévenant',
        'Principes fermes',
      ],
      weakSpotsWithOthers: [
        'Hypersensible aux conflits',
        "A besoin d'encouragement et de reconnaissance",
        'A besoin de temps pour les plaisanteries interpersonnelles',
        "Peut voir des critiques là où il n'y en a pas",
        'Peut être trop prudent et ne pas agir spontanément',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Le type de %fname est appelé INFJ. Quelques mots-clés associés : persistant, attentionné, amical',
    typeKeywords: [
      '1. Principé',
      '2. Créatif',
      '3. Réfléchi',
      '4. Organisé',
      '5. Persistant',
      '6. Consciencieux',
      '7. Compatissant',
      '8. Amical',
      '9. Attentionné',
      '10. Responsable',
      '11. Harmonieux',
    ],
    typePersonalStrengths: [
      "Bon pour analyser les problèmes d'une perspective globale",
      'Bon pour apporter de la crédibilité grâce aux idées et aux analyses',
      'Bon pour inspirer les autres avec une approche positive, humoristique et proactive',
      'Bon pour la pensée conceptuelle et abstraite',
      "Souvent doué pour l'expression créative",
      'Approche consciencieuse du travail',
      'Structuré et méthodique',
      'Grande attention aux détails',
      'Met tous ses efforts dans son travail',
      'Persévère pour atteindre le succès',
      'Bon pour fixer des objectifs',
      'Accomplit les tâches et fera ce qui est nécessaire ou souhaité',
      'Décisif dans la mise en œuvre de sa vision',
      'Forte volonté de servir le bien commun',
      'Bon pour promouvoir le changement via les relations',
      'Bon pour enseigner et aider les autres à comprendre',
      'Fort sens du devoir et de la responsabilité',
      "Conscient de l'importance de respecter les règles",
      'Achève les tâches dans les délais et avec un haut niveau de qualité',
      'Bon pour comprendre les principes théoriques sous-jacents aux problèmes',
    ],
    typeWeakSpots: [
      "S'ouvrir davantage dans les contextes sociaux",
      "Être plus sélectif quant au degré de réflexion nécessaire avant de passer à l'action",
      'Être moins évitant le conflit et moins focalisé sur la recherche du consensus',
      "Se sentir plus à l'aise pour communiquer à de grands groupes",
      "Accepter l'inconfort de prendre des décisions difficiles et impopulaires",
    ],
    description:
      "L'INFJ accorde de l'importance à la compassion et à la coopération, étant souvent de bons résolveurs de problèmes grâce à leur innovation et leur passion pour maintenir les choses organisées et complètes. En tant que type introverti, l'INFJ tend à être sensible et réservé, et garde probablement ses sentiments pour lui-même. Cependant, il fait preuve d'une grande compassion pour le bien-être des autres et possède probablement une nature profondément attentionnée. Combiné au désir de l'INFJ de garder les choses organisées, cela signifie qu'il est probable qu'il puisse accomplir les tâches sans perdre son approche personnelle et délicate. Pouvoir sentir qu'il améliore les choses est extrêmement important pour l'INFJ.",
    individualTypeByLetters: 'Protecteur',
    individualDescription:
      "En tant que Protecteur, %fname accorde de l'importance à la compassion et à la coopération. %fname est probablement un bon résolveur de problèmes grâce à son innovation et sa passion pour garder les choses organisées et accomplir les tâches. En tant que type introverti, %fname tend à être sensible et réservé, et garde ses sentiments pour lui-même. Cependant, %fname fait preuve d'une grande compassion pour le bien-être des autres et possède probablement une nature profondément attentionnée. Combiné à son désir de garder les choses organisées, il est probable qu'il puisse accomplir les tâches sans perdre son approche personnelle et délicate. Pouvoir sentir qu'il améliore les choses est extrêmement important pour %fname.",
  },
  INFP: {
    shortDescription: 'Idéaliste',
    atWorkParagraphs: {
      effectiveness: [
        "Apprendre et pratiquer l'affirmation de soi",
        'Ne pas se retirer des autres lorsque la situation devient stressante',
        'Améliorer la gestion du temps et les compétences en hiérarchisation',
        "Comprendre que les autres peuvent valoriser des résultats rapides, même s'ils ne sont pas encore idéaux",
        'Développer un respect sain pour les détails et les aspects pratiques',
        'Reconnaître la valeur des amitiés informelles et du réseautage',
        'Ne pas compliquer ou intellectualiser excessivement les problèmes',
        "Comprendre qu'ils n'ont pas besoin de plaire à tout le monde",
        "Planifier à l'avance",
        'Faire preuve de plus de détermination',
        'Exciter les autres avec leurs idées sur les possibilités à grande échelle',
      ],
      teamLeader: [
        'Fournit une motivation inspirante',
        'Se concentre sur les idéaux communs et les problèmes sous-jacents',
        "Favorise l'esprit d'équipe et l'effort collectif en créant une atmosphère positive",
        "Recherche le consensus du groupe pour faciliter l'atteinte des objectifs",
        'A une forte conscience des forces et faiblesses des individus',
        'Pousse les gens à atteindre leur potentiel',
        'Met au défi et stimule les gens',
        'Encourage les équipes à explorer de nouvelles possibilités',
        "Maintient un haut niveau de moral et un bon esprit de coopération grâce à sa capacité à 'lire' les autres",
      ],
      conflicts: [
        'Peut rencontrer des problèmes avec des personnes qui se prennent trop au sérieux',
        'Peut être irrité par les personnes qui demandent trop de détails ou dont la logique technique empêche la compréhension',
        "Peut être irrité par ceux qui refusent d'admettre qu'ils ne comprennent pas une idée",
        'Peut être irrité par ceux qui ne prennent pas en compte toutes les opinions',
      ],
      stressAdvisor: [
        'A un besoin croissant de perfectionnisme',
        "Devient focalisé uniquement sur ce qu'il considère important",
        "Perd le sens de la réalité et la simplicité lors de l'exécution d'une tâche",
      ],
      workEnvironment: ["Orienté vers l'avenir", 'Réfléchit à ses propres valeurs', "Permet de travailler par bursts d'énergie"],
      underStress: [],
      underSevereStress: [],
    },
    atWorkTagLine: 'Très collaboratif',
    atWorkSneakPeek: "Une façon d'améliorer leur efficacité au travail est d'apprendre et de pratiquer l'affirmation de soi",
    withOthersTagLine: '',
    withOthersSneakPeek: '',
    withOthersParagraphs: {
      strengths: [
        'Amical',
        'Tolérant',
        'Collaboratif',
        'Soutenant',
        'Compatissant',
        'Mystérieux',
        'Généreux dans les retours positifs',
        'Attentionné, réfléchi et bienveillant',
        'Très loyal envers les personnes qui leur sont chères',
        'Acceptant',
        'Préoccupation sincère',
      ],
      weakSpotsWithOthers: [
        "A besoin d'encouragement et de reconnaissance",
        'Préfère des relations harmonieuses et déteste les conflits',
        "Lorsqu'ils sont trop absorbés par ce qu'ils font, ils deviennent peu sociables",
        'Tend à prendre trop de choses en même temps',
        'Tend à mal gérer les personnes qui menacent leurs valeurs personnelles',
        "Trop sensible aux sentiments des autres et à l'ambiance de l'équipe",
      ],
    },
    typeTagLine: "Ouvert d'esprit, libre penseur, curieux intellectuellement",
    typeSneakPeek: "Leur type s'appelle INFP. Ils ont tendance à mal gérer les personnes qui menacent leurs valeurs personnelles",
    typeKeywords: [
      "1. Ouvert d'esprit",
      '2. Libre penseur',
      '3. Curieux intellectuellement',
      "4. Désireux d'apprendre",
      '5. Imaginatif',
      '6. Optimiste',
      '7. Guidé par les valeurs',
      '8. En développement personnel',
      '9. Explorateur',
      '10. Soutenant',
      '11. Réfléchi',
      "12. 'Vue d'ensemble'",
    ],
    typePersonalStrengths: [
      "Plein d'enthousiasme",
      'Flexible et adaptable',
      'Style de travail indépendant',
      'Responsable',
      'Très collaboratif',
      "Fort esprit d'équipe",
      'Prêt à contribuer pour le bien commun',
      'Perspicace et créatif',
      "Se soucie profondément de tout ce dans quoi il s'engage",
      'Rapide à voir les possibilités',
      'Catalyseur pour transformer les idées en réalité',
      'Capable de réaliser de nombreuses tâches proche de la date limite',
      'Bon pour promouvoir le changement',
      "Contributeur assidu à l'unité et à l'harmonie de l'équipe",
    ],
    typeWeakSpots: [
      'Ne pas chercher la perfection ou ne pas être trop idéaliste',
      'Être plus ancré dans la réalité',
      "Assouplir ses valeurs personnelles lorsqu'elles ne sont pas partagées par les autres",
    ],
    description:
      "L'INFP est probablement chaleureux et compatissant, motivé par des causes qui lui tiennent à cœur et lutte pour ce en quoi il croit. Travailler selon des valeurs personnelles est extrêmement important pour l'INFP, et lorsqu'il n'est pas guidé par un idéal ou à la recherche d'un sens plus profond, il a tendance à procrastiner et rêver. L'INFP est curieux et ouvert d'esprit, avec une approche flexible, aimant travailler en équipe afin d'introduire de nouvelles idées et aider les autres à se développer et à grandir. Une fois engagé dans une cause en laquelle il croit, l'INFP est très loyal et dévoué.",
    individualTypeByLetters: 'Idéaliste',
    individualDescription:
      "En tant qu'Idéaliste, %fname est probablement une personne chaleureuse et compatissante, motivée par des causes qui lui tiennent à cœur et lutte pour ce en quoi il croit. Travailler selon ses valeurs personnelles est extrêmement important pour %fname. Lorsqu'il n'est pas guidé par un idéal ou à la recherche d'un sens plus profond, %fname a tendance à procrastiner et rêver. %fname est curieux et ouvert d'esprit, avec une approche flexible, et aime travailler en équipe afin d'introduire de nouvelles idées et aider les autres à se développer et à grandir. Une fois engagé dans une cause qu'il croit juste, %fname est très loyal et dévoué.",
  },
  INTJ: {
    shortDescription: 'Scientifique',
    atWorkParagraphs: {
      effectiveness: [
        'Accepter le réseautage comme une partie nécessaire de leur vie professionnelle, y compris en faisant activement des connaissances et de petites discussions occasionnelles',
        'Être diplomate dans la façon dont ils formulent leurs retours, afin que les autres ne deviennent pas trop défensifs',
        "Ne pas essayer de tout faire eux-mêmes (même s'ils pensent pouvoir faire un meilleur travail) mais déléguer davantage aux autres",
        'Prêter attention aux besoins interpersonnels, y compris les leurs',
        "S'assurer de donner suffisamment d'espace aux autres pour qu'ils puissent contribuer au processus",
        "Communiquer clairement qu'ils apprécient les contributions des autres",
        "Ne pas accorder trop de valeur à l'obtention de toutes les informations avant de prendre une décision, mais accepter que certaines décisions sont mieux prises sur le moment.",
      ],
      teamLeader: [
        "Rassemble l'équipe autour d'idées et de convictions fortes",
        "Aide l'équipe à définir clairement ce qu'elle veut accomplir",
        "S'assure que l'équipe atteint son objectif et reste sur sa ligne d'action",
        "Motiver l'équipe en fixant des standards élevés",
        "Fournit à l'équipe un environnement de travail efficace et structuré",
        'Médiatise les conflits de manière objective et logique',
        'Garde un œil sur la vision globale et prend en charge le changement de stratégie si nécessaire',
      ],
      teamLeaderFlipSide: [
        'Peut être perçu comme trop argumentatif, trop axé sur les tâches ou trop contrôlant',
        'Peut aussi sembler trop réservé et détaché',
      ],
      conflicts: [
        'Peut avoir des problèmes avec les personnes qui laissent certaines tâches convenues inachevées',
        "Peut être irrité par les personnes qui semblent ne pas accorder suffisamment d'attention ou qui ne prennent pas suffisamment en compte leurs questions ou propositions",
        'Peut être irrité par les personnes qui ne suivent pas assez rapidement leurs idées et propositions',
        'Probablement frustré par les personnes qui se concentrent sur des détails insignifiants ou exigent des réponses immédiates',
      ],
      workEnvironment: [
        "Offre de l'indépendance",
        "Accorde une grande valeur à l'efficacité",
        'Est structuré et axé sur les tâches',
        "N'est pas routinier, mais offre la possibilité de changer et de réorganiser les choses",
        'Crée une stimulation intellectuelle',
      ],
      stressAdvisor: [
        'Peut devenir de plus en plus inflexible et progressivement critique',
        "Peut être rigide dans ses opinions et points de vue, avoir du mal à accepter s'il a tort",
        "Peut devenir impatient et chercher des erreurs même s'il n'y en a pas",
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek: "Une façon d'améliorer leur efficacité au travail est de ne pas essayer de tout faire eux-mêmes",
    withOthersTagLine: '---',
    withOthersSneakPeek:
      "Ils pourraient se sentir le plus à l'aise en occupant les rôles suivants dans leur équipe : Le Précis, Le Générateur d'idées, Le Critique",
    withOthersParagraphs: {
      strengths: [
        'Indépendant',
        'Sceptique',
        'Critique',
        'Normes élevées de compétence et de performance pour eux-mêmes et pour les autres',
        'Calme',
        'Observateur',
        'Déterminé',
        'Principé',
        'Argumentatif',
        'Fiable',
        'Réfléchi',
        'Logique',
      ],
      weakSpotsWithOthers: [
        'Peut parfois être trop introspectif',
        'Comprendre les sentiments des autres peut ne pas toujours être naturel',
        "En tant que personne probablement perçue comme calme, lorsqu'ils expriment fortement leurs opinions, les collègues peuvent être surpris",
        'Peut parfois être trop obstiné',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Leur type est appelé INTJ. Les autres peuvent les décrire comme persévérants, attentionnés ou amicaux',
    typeKeywords: [
      '1. Logique',
      '2. Compétent',
      '3. Dévoué',
      '4. Orienté objectif',
      '5. Motivé',
      '6. Personne à idées',
      '7. Méthodique',
      '8. Organisé',
      '9. Fiable',
      '10. Individualiste',
      '11. Principé',
    ],
    typePersonalStrengths: [
      'Bon à donner des conseils',
      'Accomplit les choses',
      'Orienté objectif',
      'Fournit des résultats comme prévu',
      'Prend une perspective à long terme et résout les problèmes avec des solutions durables',
      'Fort sens du devoir et des responsabilités',
      'Fort en pensée systématique et structurée',
      'Fort en analyse objective et logique',
      "Capable d'apprendre par soi-même",
      'Approche méthodique et organisée pour résoudre les problèmes',
      'Fixe des standards élevés pour eux-mêmes et pour les autres',
      'Capable de prendre des décisions difficiles',
      'Original',
      'Capable de mener les choses à terme',
      'Bon pour développer de nouvelles théories, modèles, idées ou systèmes',
      'Bon pour accomplir les tâches à un haut niveau et dans les délais',
      'Bon pour suivre les méthodes et procédures établies',
      'Attentif aux détails',
      'Travaille avec des concepts et systèmes théoriques',
      'Fiable',
      'Non-conformiste',
      'Curieux intellectuellement',
    ],
    typeWeakSpots: [
      "Peut irriter les autres en étant trop focalisé sur la poursuite d'une tâche ou d'un objectif",
      'Préfère être celui qui est responsable, assumant parfois une charge plus lourde que ce qui est soutenable',
    ],
    description:
      "L'INTJ est probablement rapide et imaginatif, avec un esprit stratégique. Il est susceptible d'apprécier passer son temps à réfléchir et analyser l'information de manière attentive et réfléchie. Le système et la stratégie sont probablement importants pour l'INTJ, l'organisation et la planification étant des éléments clés pour l'aider à comprendre comment les choses fonctionnent. Les idées abstraites et les problèmes logiques séduisent l'INTJ, qui passera du temps à considérer et questionner ces concepts. En se concentrant sur le tangible et le rationnel, l'INTJ a une vision et un esprit d'innovation et est capable de travailler vers les résultats. L'INTJ ne tend pas à partager facilement ses émotions avec les autres.",
    individualTypeByLetters: 'Scientifique',
    individualDescription:
      "En tant que Scientifique, %fname est probablement rapide et imaginatif, avec un esprit stratégique. %fname est susceptible d'apprécier passer du temps à réfléchir et analyser l'information de manière attentive et réfléchie. Le système et la stratégie sont probablement importants pour eux, et l'organisation et la planification sont des éléments clés pour les aider à comprendre comment les choses fonctionnent. Les idées abstraites et les problèmes logiques séduisent %fname, qui est susceptible de passer du temps à considérer et questionner ces concepts. En se concentrant sur le tangible et le rationnel, %fname est probablement visionnaire et innovant et a la capacité de travailler vers les résultats. %fname ne tend pas à partager facilement ses émotions avec les autres.",
  },
  INTP: {
    shortDescription: 'Penseur',
    atWorkParagraphs: {
      effectiveness: [
        'Faire un effort conscient pour contacter les gens plus souvent',
        'Reconnaître la valeur de participer à de petites discussions',
        'Utiliser un langage plus simple',
        'Réduire les choses à une hypothèse logique',
        "Prendre en compte plus souvent les sentiments et émotions des gens dans l'analyse des problèmes et la considération des tâches",
        'Apprendre des techniques de gestion du temps',
        'Se concentrer sur les aspects pratiques',
      ],
      teamLeader: [
        "Utilise la logique et la raison pour guider les membres de l'équipe dans la bonne direction",
        "Met en place des cadres qui permettent à tous les membres de l'équipe d'utiliser leurs compétences",
        'Est perçu comme une source de connaissance et de compétence, agissant comme une ressource active pour les autres',
        "Définit l'agenda et maintient les autres concentrés sur celui-ci",
        'Fournit des options afin que les décisions reflètent différentes opinions et aboutissent à la meilleure solution possible et acceptable',
        'Excelle à résumer et généraliser',
        'Motiver son équipe en fixant des standards élevés',
      ],
      conflicts: [
        "Probablement irrité par les membres de l'équipe qui sont peu concentrés et illogiques dans leur approche",
        'Probablement trouvera les membres qui argumentent par ignorance difficiles à gérer',
        'Probablement frustré par les membres qui, selon lui, prennent les commentaires objectifs personnellement et sont trop facilement blessés ou offensés par ce qui est logique',
        "Probablement aura des difficultés à travailler avec des personnes qui manquent de vision et d'engagement dans le projet",
        'Peut avoir une faible tolérance envers les personnes qui parlent beaucoup et se concentrent sur des sujets non essentiels au détriment de la mission globale',
      ],
      stressAdvisor: [
        'Peut devenir sarcastique et impatient',
        'Peut analyser de plus en plus les choses et avoir des débats sans fin',
        'Peut être rigide dans ses points de vue. Peut devenir de plus en plus indépendant et négatif',
      ],
      workEnvironment: [
        'Un environnement qui encourage les idées et la créativité',
        "Où il y a moins d'interruptions et une supervision minimale",
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      "Une façon pour %fname d'améliorer son efficacité au travail est de faire un effort conscient pour contacter les gens plus souvent.",
    withOthersTagLine: '---',
    withOthersSneakPeek:
      "%fname pourrait se sentir le plus à l'aise en occupant les rôles suivants dans son équipe : Le Générateur d'idées, Le Critique, L'Organisateur",
    withOthersParagraphs: {
      strengths: [
        'Aime se challenger et challenger les autres',
        "Préfère parler d'idées plutôt que de bavardage",
        "Ouvert d'esprit, libre penseur et tolérant",
        'Valorise la logique et la pensée rationnelle',
        'Peut être enthousiaste',
        'Amical et perspicace',
        'Réfléchi',
        'Préférant communiquer par écrit',
        'Probablement perçu comme un penseur profond',
      ],
      weakSpotsWithOthers: [
        'Peut sembler calme et réservé au début',
        'Peut paraître trop argumentatif',
        'Peut sembler froid et fermé',
        'Peut être perçu comme peu pratique, désorganisé et distrait',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Leur type est appelé INTP. Quelques mots-clés associés sont Visionnaire, Compétitif, Analytique',
    typeKeywords: [
      '1. Visionnaire',
      '2. Compétence',
      '3. Curiosité',
      '4. Expert',
      '5. Connaissant',
      '6. Logique',
      '7. Enthousiaste',
      '8. Compétitif',
      '9. Réfléchi',
      '10. Flexible',
      '11. Analytique',
      "12. Personne ayant une vue d'ensemble",
    ],
    typePersonalStrengths: [
      'Bon pour appliquer ses connaissances',
      'Bon pour résoudre des problèmes avec des solutions durables',
      'Personne très performante',
      'Probablement amené à devenir un expert dans un domaine spécifique',
      "Souvent doté d'une capacité inhabituelle à résoudre des problèmes complexes dans son domaine d'intérêt",
      "Bon pour percevoir les schémas, les relations et voir la vue d'ensemble",
      'Fort dans le développement de nouvelles théories, modèles, idées ou systèmes',
      'Bon pour fournir à la fois une vision critique et une perspicacité intellectuelle',
      'Fortes capacités conceptuelles de résolution de problèmes',
      'Bon pour collecter des données de manière impartiale',
      'Très analytique',
      'Créatif et imaginatif',
      "Aime remettre en question et critiquer l'ordre des choses",
      'Flexible et adaptable',
      "Apprécie les opportunités d'être créatif",
      'Aime la complexité',
      'Indépendant',
      'Architecte du changement',
      'Concepteur de systèmes',
    ],
    typeWeakSpots: [
      'Montrer moins de scepticisme et de critique',
      'Communiquer davantage et de manière plus ouverte ou amicale',
      "Explorer d'autres intérêts. Être dans une carrière qui leur permet de mettre leurs intérêts à profit",
      'Accepter davantage les personnes moins compétentes',
      'Se conformer davantage dans certaines situations',
      'Peut parfois rencontrer des doutes sur soi',
      'Peut préférer travailler seul',
      "Bon pour trouver des solutions créatives et novatrices, mais moins enthousiaste lorsqu'il s'agit de les mettre en œuvre",
    ],
    description:
      "L'INTP est probablement quelqu'un qui aime réfléchir et résoudre des problèmes grâce à sa tendance à avoir un bon œil pour les détails et une aptitude à comprendre les idées abstraites et les problèmes logiques. L'INTP est probablement calme et réfléchi, préférant garder ses sentiments pour lui-même. En tant qu'individu objectif et réaliste, il tend à apprécier les débats avec les autres, ce qui lui permet de questionner et de chercher la vérité ; cependant, il peut parfois sembler vague ou négliger les besoins des autres s'il est trop absorbé par ce qu'il considère important.",
    individualTypeByLetters: 'Penseur',
    individualDescription:
      "En tant que Penseur, %fname est probablement quelqu'un qui aime réfléchir et résoudre des problèmes grâce à sa tendance à avoir un bon œil pour les détails et une aptitude à comprendre les idées abstraites et les problèmes logiques. %fname est probablement calme et réfléchi, préférant garder ses sentiments pour lui-même. En tant qu'individu objectif et réaliste, %fname tend à apprécier les débats avec les autres, ce qui lui permet de questionner et de chercher la vérité. Cependant, %fname peut parfois sembler vague ou négliger les besoins des autres s'il est trop absorbé par ce que %fname considère comme important.",
  },
  ISFJ: {
    shortDescription: 'Protecteur',
    atWorkParagraphs: {
      effectiveness: [
        "Ne pas être trop sensible et préoccupé par l'harmonie",
        'Louer plus souvent ses propres compétences ainsi que celles des autres, plutôt que de les minimiser',
        'Être prêt à transmettre un message difficile lorsque nécessaire',
        'Ne pas prendre trop au sérieux ou personnellement les remarques des autres, mais les laisser passer plus souvent',
        "S'en tenir à son propre avis s'ils sont convaincus d'avoir raison, plutôt que de suivre la majorité pour le bien de l'harmonie de l'équipe",
      ],
      teamLeader: [
        'Travaille dur et est toujours préparé',
        'Tendance à bien écouter toutes les idées, rassembler tout le monde et obtenir un soutien global pour la solution',
        "Fait travailler les autres ensemble au sein du système et respecter les règles qu'ils établissent",
        'Encourage les autres à développer leurs propres compétences en leadership dès que possible',
        "Insiste sur la précision et l'exactitude",
      ],
      conflicts: [
        "Probablement irrité par les membres de l'équipe qui, selon lui, parlent trop, interrompent ou n'écoutent pas les autres",
        'Probablement dérangé par les membres qui dénigrent les autres et leurs idées',
        "Peut être irrité par les personnes qui, selon lui, passent trop de temps sur des agendas personnels et négligent ainsi les tâches de l'équipe et les objectifs des réunions",
        'Peut avoir des conflits avec les personnes qui, selon lui, manquent de bon sens',
        'Aura probablement des difficultés avec les personnes qui, selon lui, ne sont pas disposées à collaborer ou à partager des informations pertinentes',
      ],
      stressAdvisor: [
        'Trouve très difficile de dire non et devient de plus en plus pessimiste si les choses ne se passent pas comme il le souhaite',
        "Devient de plus en plus anxieux et, par conséquent, plus rigide dans sa pensée afin d'essayer de contrôler la situation",
        "Peut se sentir découragé s'il ne se sent pas apprécié",
      ],
      workEnvironment: [
        "Un environnement harmonieux est préféré, afin d'éviter les conflits",
        'Préférence pour une organisation structurée avec des rôles et tâches définis',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      "Une façon pour %fname d'améliorer son efficacité au travail est d'être prêt à transmettre un message difficile lorsque nécessaire.",
    withOthersTagLine: '---',
    withOthersSneakPeek:
      "%fname pourrait se sentir le plus à l'aise en occupant les rôles suivants dans une équipe : Le Précis, L'Organisateur, Le Critique",
    withOthersParagraphs: {
      strengths: [
        'Amical et responsable',
        "Attentionné et bienveillant, toujours présent lorsque les autres ont besoin d'eux",
        'Chaleur discrète et humour doux',
        'Se souvient des détails concernant les personnes qui leur sont importantes',
        'Bon pour apaiser les conflits',
        'Loyal, attentionné et perspicace',
        'Souci de la manière dont les autres se sentent',
        'Réfléchi en silence, empathique et sensible',
        "Aime être précis dans les conversations et s'attend à la même chose des autres",
      ],
      weakSpotsWithOthers: [
        'Forte désir de stabilité',
        'Peut être trop sensible',
        "Peut se concentrer trop sur l'ici et maintenant",
        'Fort dégoût du conflit, donc préférence marquée pour des relations harmonieuses',
        "Sensible aux sentiments des autres et à l'humeur de l'équipe",
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Le type de %fname est appelé ISFJ. Quelques mots-clés associés sont Pratique, Ordonné, Précis',
    typeKeywords: [
      '1. Pratique',
      '2. Systématique',
      '3. Fiable',
      '4. Réaliste',
      '5. Bienveillant',
      '6. Sensible',
      '7. Minutieux',
      '8. Ordonné',
      '9. Décisif',
      '10. Amical',
      '11. Stable',
      '12. Précis',
    ],
    typePersonalStrengths: [
      'Prêt à faire ce qui doit être fait ici et maintenant',
      'Pratique avec un bon sens commun',
      'Bon pour accomplir les tâches à un haut niveau',
      'Prend le travail au sérieux et a un fort sens des responsabilités',
      "Observateur réaliste de l'environnement",
      'Préfère apprendre en faisant',
      'Forte attention aux détails',
      'Bonne mémoire et solide connaissance des faits',
      'Patient pour rassembler les détails nécessaires',
      'Bonnes compétences organisationnelles',
      'Apporte de la stabilité à tout projet ou groupe',
      'Approche systématique et approfondie',
      'Décisif',
      "Très loyal lorsqu'il se sent intégré",
      "Bon pour créer de l'ordre",
      'Bon pour fournir des informations de contexte de manière réactive et consciencieuse',
    ],
    typeWeakSpots: [
      "Essayer d'être plus ouvert ou amical",
      "Accepter davantage le conflit et ne pas toujours rechercher l'harmonie",
      "Être moins dépendant de l'autorité",
      'Accepter davantage le changement',
      "Accepter qu'ils soient parfois mal à l'aise avec le désordre et l'incertitude",
    ],
    description:
      "L'ISFJ est probablement une personne attentionnée et compatissante qui cherche à se sentir utile et serviable. Elle est dévouée et patiente ; quelqu'un qui n'abandonne pas au premier obstacle. Cependant, elle est probablement assez timide et une personne calme. L'ISFJ pense aux autres avant elle-même et apporte un soutien amical et pratique, privilégiant une approche plus personnelle. Elle aime prendre soin des détails pratiques du quotidien et éviter d'attirer l'attention sur elle-même.",
    individualTypeByLetters: 'Protecteur',
    individualDescription:
      "En tant que Protecteur, %fname est probablement considéré comme une personne attentionnée et compatissante. %fname aime se sentir utile et serviable, et est probablement dévoué et patient ; quelqu'un qui n'abandonne pas au premier obstacle. %fname est probablement assez timide et une personne calme, pensant aux autres avant lui-même et apportant un soutien amical et pratique, préférant une approche plus personnelle. %fname est probablement attentif aux détails pratiques du quotidien et évite d'attirer l'attention sur lui-même.",
  },
  ISFP: {
    shortDescription: 'Artiste',
    atWorkParagraphs: {
      effectiveness: [
        "Ne pas être trop modeste et reconnaître ses mérites lorsque c'est justifié",
        'Ne pas minimiser ses propres compétences et contributions',
        'Ne pas prendre trop au sérieux les commentaires des autres, en reconnaissant que certaines personnes ne réfléchissent pas avant de parler',
        'Continuer à être agréable, tout en apprenant à montrer un peu de fermeté lorsque nécessaire',
        "Ne pas se soucier excessivement de l'harmonie",
        'Apprendre à transmettre un message difficile lorsque nécessaire',
        'Ne pas être trop autocritique',
        'Apprendre des techniques de gestion du temps',
        "Apprendre à dire 'Non'",
        "S'exercer à donner des retours négatifs",
      ],
      teamLeader: [
        'Inspire les autres à suivre leur code de travail acharné et toujours être préparé',
        'Écoute attentivement toutes les idées et préoccupations, puis obtient un soutien global pour une solution grâce à une persuasion douce mais efficace',
        'Montre un intérêt sincère pour les autres points de vue',
        'Permet aux autres une grande liberté pour fixer leurs propres objectifs',
        'Ne prend des décisions que si un consensus a été atteint',
      ],
      conflicts: [
        'Probablement gêné par les personnes qui parlent trop',
        'Probablement en conflit avec ceux qui dénigrent les autres et leurs idées',
        'Peut être irrité par les personnes qui manquent de bon sens',
        "Peut être contrarié par ceux qui freinent l'équipe en ne partageant pas les informations pertinentes",
        "N'apprécie pas les personnes égoïstes et non coopératives",
      ],
      stressAdvisor: [
        'Peut devenir de plus en plus désorganisé, avoir des difficultés avec la gestion du temps et laisser les autres prendre le contrôle pour éviter les responsabilités',
        "Peut prendre les critiques personnellement, même si ce n'est qu'une question de différences d'opinions",
        "Met du temps à prendre une décision et change fréquemment d'avis",
      ],
      workEnvironment: [
        'Un environnement flexible avec peu de supervision',
        "Préfère un cadre de travail soutenant et significatif pour se sentir à l'aise",
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      "Une façon pour %fname d'améliorer son efficacité au travail est de ne pas minimiser ses propres compétences et contributions.",
    withOthersTagLine: '---',
    withOthersSneakPeek:
      "%fname pourrait se sentir le plus à l'aise en occupant les rôles suivants dans une équipe : Le Modérateur, L'Organisateur, Le Précis",
    withOthersParagraphs: {
      strengths: [
        'Discrètement amical, chaleureux et soutenant',
        'Loyal et engagé envers les personnes importantes',
        'Sensible',
        'Gentil',
        "Ouvert d'esprit",
        "Fait preuve d'un humour doux",
        'Non jugeant et peu opiniâtre',
        'Réfléchi',
        'Peu susceptible de critiquer inutilement les autres',
        'Ne force pas ses opinions ou valeurs aux autres',
      ],
      weakSpotsWithOthers: [
        "Peut mettre du temps à s'ouvrir à de nouvelles personnes",
        'Évite les désaccords',
        'Impulsif',
        'Peu bavard ou affirmé',
        'Peut se retirer des autres en situation de stress',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Le type de %fname est appelé ISFP. Quelques mots-clés associés sont Pratique, Attentionné, Flexible',
    typeKeywords: [
      '1. Pratique',
      "2. Ouvert d'esprit",
      '3. Indépendant',
      '4. Doux',
      '5. Positif',
      '6. Attentionné',
      '7. Détendu',
      '8. Pratique',
      '9. Flexible',
      '10. Réaliste',
      '11. Loyal',
    ],
    typePersonalStrengths: [
      "Préfère l'action à la parole",
      'Soucieux de trouver des solutions pratiques et concrètes aux problèmes réels',
      "Tolérant, ouvert d'esprit et acceptant les points de vue des autres",
      'Capable de travailler de manière autonome et selon son propre rythme',
      "Flexible et adaptable, ne s'accroche pas aux idées dépassées",
      'Loyal et engagé',
      'Bon pour faire collaborer les autres au sein du système',
      'Bon pour fournir des informations de contexte de manière réactive et consciencieuse',
      "Apprécie l'expérience pratique",
      'Réaliste',
      'Bon pour réagir en situation de crise et résoudre les problèmes',
      "Attentionné et capable d'anticiper l'impact de ses actions sur les autres",
      'Indépendant dans sa pensée',
      'Attitude positive',
      'Bon pour inclure les autres et apaiser les conflits',
      'Préfère travailler de manière égalitaire',
      'A des valeurs et idéaux solides',
      'Bon pour la communication individuelle',
      "Bon pour voir la vue d'ensemble",
      'Prêt à contourner les règles si nécessaire',
      "Bon pour l'observation perspicace",
    ],
    typeWeakSpots: [
      "Avoir plus d'urgence pour accomplir les tâches",
      'Être plus ouvert ou partager ses pensées',
      'Apprécier davantage les situations de désaccord et de conflit. Ne pas toujours rechercher le compromis et le consensus',
      "Apprécier la routine et les règles, et moins de liberté ou d'espace",
      'Être moins modeste sur ses capacités',
      'Prendre davantage les initiatives',
      'Faire plus attention aux détails',
    ],
    description:
      "L'ISFP est une personne décontractée, calme et amicale. Elle est coopérative, préférant un rôle de soutien pour promouvoir l'harmonie de l'équipe. Orientée vers l'action, elle préfère les activités pratiques pour mettre sa créativité à profit, mais elle a tendance à éviter les activités d'organisation et de planification, privilégiant une approche plus flexible et spontanée et évitant les projections futures. Bonne écoute, tolérante et sensible aux besoins des autres, elle fournira des conseils et un soutien pratiques lorsque possible.",
    individualTypeByLetters: 'Artiste',
    individualDescription:
      "En tant qu'Artiste, %fname est probablement une personne décontractée, calme et amicale. %fname a tendance à être coopératif, appréciant un rôle de soutien pour promouvoir l'harmonie de l'équipe. %fname est plus orienté vers l'action, préférant les activités pratiques pour mettre sa créativité à profit. Cependant, il est probable qu'il évite les activités d'organisation et de planification, privilégiant une approche plus flexible et spontanée et évitant de faire des projets pour l'avenir. %fname est probablement un bon auditeur, tolérant et sensible aux besoins des autres, à l'aise pour donner des conseils pratiques et un soutien chaque fois que possible.",
  },
  ISTJ: {
    shortDescription: 'Inspecteur',
    atWorkParagraphs: {
      effectiveness: [
        'S’assurer de tenir les gens informés et à jour',
        'Développer leurs compétences en réseautage',
        'Essayer de créer des moments agréables pendant la journée de travail',
        'Reconnaître la valeur des conversations informelles',
        'Demander des retours plus souvent',
        'Encourager et apprécier la créativité',
        'Développer des compétences en communication interpersonnelle',
        'Se rappeler de ne pas être trop absorbé par les détails individuels au point de perdre le sens global de la tâche',
        'Aller au-delà des systèmes établis',
        'Déléguer davantage et moins microgérer',
        'Participer aux discussions au début d’un processus et exprimer leurs points de vue plus tôt plutôt que d’attendre qu’il soit trop tard',
        'Suspendre le jugement critique pendant les séances de remue-méninges',
      ],
      teamLeader: [
        'Inspire la confiance de son équipe par des arguments logiques, une connaissance détaillée du sujet et une vision réaliste',
        'Est ouvert aux faits et aux arguments logiques',
        'Organise et maintient une structure efficace sans trop parler',
        'Dirige des réunions très efficaces et ciblées',
        'Est clair sur ce qui est attendu, les tâches à accomplir et le niveau de qualité requis',
      ],
      conflicts: [
        'Est probablement gêné par les membres de l’équipe qu’il perçoit comme trop bavards',
        'Peut être irrité par les personnes perçues comme peu fiables et manquant de suivi',
        'A probablement une faible tolérance envers ceux qui veulent discuter de problèmes personnels ou sans rapport avec le travail',
      ],
      stressAdvisor: [
        'Peut devenir particulièrement concentré et inflexible, exigeant que les règles et structures soient respectées',
        'Peut avoir du mal à accepter d’avoir tort et à accorder suffisamment d’éloges aux autres lorsqu’ils le méritent',
        'Peut devenir pessimiste et excessivement pointilleux sur les détails',
      ],
      workEnvironment: [
        'Préfère un travail avec des objectifs et des procédures clairs',
        'Préfère travailler sur une seule tâche ou un seul projet à la fois, avec une supervision minimale',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek:
      'Une façon pour %fname d’améliorer son efficacité au travail est de reconnaître la valeur des conversations informelles',
    withOthersTagLine: '---',
    withOthersSneakPeek:
      '%fname pourrait se sentir le plus à l’aise en assumant les rôles suivants dans une équipe : Le Critique, Le Précisien, L’Organisateur',
    withOthersParagraphs: {
      strengths: [
        'Valorise les traditions et la loyauté',
        'Un sens du devoir',
        'Réaliste et pragmatique',
        'Calme et indépendant',
        'Fiable et digne de confiance',
        'Prêt à aider',
        'Ferme dans ses opinions',
        'Préfère communiquer par écrit plutôt que d’avoir de longues discussions',
        'Traite les gens avec respect',
      ],
      weakSpotsWithOthers: [
        'Peut sembler réservé et peu émotif',
        'Pas très sociable, préférant accomplir les tâches plutôt que d’échanger des politesses',
        'Difficile à connecter et à comprendre émotionnellement',
        'Peut être perçu comme manquant d’imagination et rigide',
        'Lent à s’adapter et à réagir au changement',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Le type de %fname est appelé ISTJ. Quelques mots-clés associés sont stable, objectif, décisif',
    typeKeywords: [
      '1. Précis',
      '2. Décisif',
      '3. Raisonnable',
      '4. Responsable',
      '5. Respectueux',
      '6. Stable',
      '7. Détailleur',
      '8. Fiable',
      '9. Méthodique',
      '10. Amical',
      '11. Informé',
      '12. Objectif',
    ],
    typePersonalStrengths: [
      'Ordonné et organisé, à la maison comme au travail',
      'Approche responsable du travail',
      'Engagé à accomplir les tâches',
      'Accomplit les tâches avec minutie',
      'Vision pratique et réaliste',
      'Bon pour résoudre les problèmes avec bon sens',
      'Travaille régulièrement pour atteindre les objectifs, malgré les distractions',
      'Décisif',
      'Objectif et rationnel',
      'Solides compétences analytiques',
      'Engagé envers les personnes et l’organisation dont il fait partie',
      'Discipliné et apprécie de développer et suivre une routine',
      'Précis et excellente maîtrise des faits',
      'Grande attention aux détails',
      'Prêt à travailler de manière indépendante',
      'Valorise le respect des processus et procédures établis',
      'Bon pour faire respecter les règles et politiques',
      'Bon pour documenter les procédures et informations',
      'Aime améliorer les choses par petites étapes',
      'Normes de qualité élevées. Bon pour identifier et corriger les erreurs',
      'Apporte une grande responsabilité à toute équipe',
      'Travailleur acharné, inspire les autres à travailler dur pour terminer la tâche à temps',
    ],
    typeWeakSpots: [
      'Apprécier ou accepter davantage l’instabilité et le désordre',
      'Accepter davantage le changement et l’innovation',
      'Être moins attaché aux règles',
    ],
    description:
      'L’ISTJ est une personne systématique, particulièrement loyale et fiable. Elle est fière du travail qu’elle accomplit et se sent à l’aise avec les tâches routinières. Les ISTJ sont des personnes sérieuses et réfléchies, qui aiment créer et faire respecter l’ordre. Ils sont précis, méthodiques et responsables, et aiment traiter les faits. Les responsabilités sont bienvenues, et l’ISTJ prendra en charge la tâche à accomplir, respectera les délais et aura peu de tolérance pour l’indécision et la paresse. Le devoir est important pour l’ISTJ, et une fois engagé dans un projet, il mènera les actions à terme, en s’assurant que les décisions reposent sur des faits et de la logique.',
    individualTypeByLetters: 'Inspecteur',
    individualDescription:
      'En tant qu’Inspecteur, %fname est probablement une personne systématique, particulièrement loyale et fiable. %fname est fier du travail qu’il accomplit et se sent à l’aise avec les tâches routinières. %fname peut être perçu comme une personne sérieuse et réfléchie, qui aime créer et faire respecter l’ordre ; probablement précis, méthodique, responsable et appréciant de traiter les faits. %fname accueille probablement les responsabilités et prend en charge les tâches, respectant les délais et ayant peu de tolérance pour l’indécision et la paresse. Le devoir est sans doute important pour lui et, une fois engagé dans un projet, il a tendance à aller jusqu’au bout, en veillant à ce que les décisions reposent sur les faits et la logique.',
  },
  ISTP: {
    shortDescription: 'Mécanicien',
    atWorkParagraphs: {
      effectiveness: [
        'Veiller à ne pas travailler sur trop de tâches simultanément. Terminer les tâches clés avant de passer à quelque chose de nouveau',
        'Être moins pointilleux sur les détails spécifiques',
        "Faire attention à ne pas donner l'impression d'être indifférent aux autres",
        "Concentrer leur énergie sur la résolution d'une crise à la fois",
        "Établir une structure stricte qui limite les options d'action",
        "Passer plus de temps à se préparer à l'avance",
        'Préparer un ordre du jour pour les réunions',
        'Pratiquer leurs compétences en réseautage',
        'Prendre plus souvent en compte les besoins interpersonnels des collègues',
        "Prêter plus d'attention aux détails",
        'Reconnaître la valeur des conversations informelles',
      ],
      teamLeader: [
        'Adopte une approche pragmatique du leadership',
        "Dirige l'équipe avec une autorité calme et indirecte",
        "Évalue tous les points de vue, mais avec l'intention claire de passer à l'action",
        "Encourage l'équipe à persévérer",
        'Gère très bien les crises et le changement',
        'Sait où trouver les informations nécessaires',
        "Résout les problèmes en appliquant la logique et en examinant d'abord les faits",
      ],
      conflicts: [
        "A probablement du mal à travailler avec des membres d'équipe qu'il perçoit comme trop émotionnels plutôt qu'objectifs",
        "Peut avoir des difficultés avec des personnes qu'il perçoit comme gaspillant de l'énergie précieuse sur des tâches hors du champ de l'équipe",
        "Peut être irrité par les membres de l'équipe qui organisent fréquemment des réunions informatives sans objectif pratique clair",
      ],
      stressAdvisor: [
        'Peut être sujet à des explosions émotionnelles et impulsives sous stress et pression',
        'Peut avoir du mal à suivre et à terminer une tâche',
        'Trop concentré sur les détails actuels, sans considérer les possibilités futures',
      ],
      workEnvironment: [
        'Cherche la flexibilité face à la routine et nécessite peu de supervision car aime agir spontanément',
        'Aime jouer avec les idées et trouver des moyens novateurs de résoudre les problèmes',
      ],
    },
    atWorkTagLine: '---',
    atWorkSneakPeek: "Une façon pour %fname d'améliorer son efficacité au travail est de se concentrer moins sur les détails spécifiques",
    withOthersTagLine: '---',
    withOthersSneakPeek:
      "%fname pourrait se sentir le plus à l'aise en assumant les rôles suivants dans son équipe : Le Critique, Le Précisien, L'Organisateur",
    withOthersParagraphs: {
      strengths: [
        "Tolérant et ouvert d'esprit",
        "Éclairs inattendus d'humour original",
        'Tend à analyser la vie avec curiosité',
        "Aime vivre l'instant présent",
        'Orienté action et spontané',
        "Aime s'amuser",
        'Profond',
        'Réfléchi',
        'Pensif',
        'Autonome',
        'Peut préférer communiquer par écrit',
        'Juste et objectif',
      ],
      weakSpotsWithOthers: [
        'Peut être perçu comme trop impulsif',
        'Peut se sentir trop facilement restreint',
        "Peut attendre avant d'être prêt à contribuer",
        'Les personnes plus émotionnelles auront du mal avec son approche très rationnelle',
        'Peut être perçu comme réservé',
      ],
    },
    typeTagLine: '--',
    typeSneakPeek: 'Le type de %fname est appelé ISTP. Quelques mots-clés associés sont objectif, logique, ou tolérant',
    typeKeywords: [
      '1. Terre-à-terre',
      '2. Logique',
      '3. Analytique',
      '4. Factuel',
      '5. Adaptable',
      '6. Pragmatique',
      '7. Tolérant',
      '8. Indépendant',
      '9. Objectif',
      '10. Juste',
      '11. Résolveur de problèmes',
    ],
    typePersonalStrengths: [
      'Bon pour comprendre comment les choses fonctionnent',
      "Bon pour mobiliser l'équipe à l'action",
      'Forte concentration sur des solutions pratiques',
      'Résolveur de problèmes naturel : rapide pour trouver des solutions applicables',
      'Bon pour utiliser ses compétences analytiques pour organiser le travail efficacement',
      'Flexible et adaptable',
      'Bon pour organiser les faits et appliquer des principes logiques',
      'Approche pragmatique, réaliste et pratique',
      'Forte pensée logique',
      'Bon pour planifier',
      "Prêt pour l'excitation et l'aventure",
      'Bon pour répondre aux crises',
      'Valorise la logique et la pensée rationnelle',
      'Prêt à agir',
      'Réactif et persévérant',
      'Bon pour travailler de manière indépendante',
      "Prêt à enfreindre les règles si elles n'ont pas de sens",
      'Réaliste orienté solutions',
      "À l'aise avec les faits et les données concrètes",
    ],
    typeWeakSpots: [
      'Essayer de ne pas rester en retrait et observer',
      'Essayer de paraître moins détaché',
      "Ne pas ignorer les routines à la recherche de variété et d'excitation",
      "S'intéresser davantage aux théories abstraites",
    ],
    description:
      "L'ISTP est probablement attiré par un rôle nécessitant de l'action. Il est probablement pratique, très enthousiaste et énergique à propos d'un projet. L'ISTP est un individu assez détendu, préférant la flexibilité à l'organisation et laissant sa curiosité le mener vers de nouveaux horizons. Tendant à être logique et pratique, avec une soif de connaissance, l'ISTP n'est pas particulièrement à l'aise avec la planification et l'organisation, préférant une approche plus flexible.",
    individualTypeByLetters: 'Mécanicien',
    individualDescription:
      "En tant que Mécanicien, %fname est probablement attiré par un rôle nécessitant de l'action. %fname aime être pratique et est très enthousiaste et énergique à propos d'un projet. Il est généralement un individu assez détendu, préférant la flexibilité à l'organisation et laissant sa curiosité le guider vers de nouveaux horizons. %fname est plus logique et pratique, avec une soif de connaissance et n'est pas particulièrement à l'aise avec la planification ou l'organisation. %fname préfère une approche plus flexible.",
  },
};

export const tmiTypeGroupsFr: { name: string; types: string[] }[] = [
  {
    name: 'Artisans',
    types: ['ISFP', 'ESTP', 'ESFP', 'ISTP'],
  },
  {
    name: 'Gardiens',
    types: ['ESTJ', 'ISTJ', 'ESFJ', 'ISFJ'],
  },
  {
    name: 'Idéalistes',
    types: ['ENFJ', 'INFJ', 'ENFP', 'INFP'],
  },
  {
    name: 'Rationnels',
    types: ['ENTJ', 'INTJ', 'ENTP', 'INTP'],
  },
];

export const spectrumTraitsFr: {
  [trait: string]: {
    imageUrl: string;
    left: {
      title: string;
      description: string;
    };
    right: {
      title: string;
      description: string;
    };
  };
} = {
  OPPORTUNITY_AWARENESS: {
    imageUrl: lightningImageUrl,
    left: {
      title: 'Conscience des opportunités',
      description: 'Identifie les possibilités et les opportunités',
    },
    right: {
      title: 'Catégorique',
      description: 'Voit les choses comme définitives et claires',
    },
  },
  ACTION_ORIENTATION: {
    imageUrl: lightningImageUrl,
    left: {
      title: 'Orienté action',
      description: "Autonome, énergique, passe rapidement à l'action",
    },
    right: {
      title: 'Tentatif',
      description: 'Réfléchit avant d’agir, patient',
    },
  },
  DRIVE: {
    imageUrl: lightningImageUrl,
    left: {
      title: 'Accommodant',
      description: 'Régulier, peu exigeant',
    },
    right: {
      title: 'Motivé par la réussite',
      description: 'Ambitieux, cherche le défi, sous haute pression',
    },
  },
  SELF_EFFICACY: {
    imageUrl: lightningImageUrl,
    left: {
      title: 'Inquiet',
      description: 'Peut percevoir les objectifs difficiles comme inaccessibles et irréalisables',
    },
    right: {
      title: 'Confiant',
      description: 'Croît en sa propre compétence pour relever de nouveaux défis',
    },
  },
  PERSISTENCE: {
    imageUrl: courtImageUrl,
    left: {
      title: 'Immédiat',
      description: 'Recherche des réussites immédiates et réalisables',
    },
    right: {
      title: 'Persévérant',
      description: 'Poursuit des tâches difficiles, peut négliger le moment présent',
    },
  },
  ORDERLINESS: {
    imageUrl: courtImageUrl,
    left: {
      title: 'Consciencieux',
      description: 'Bien organisé, planifié, ordonné',
    },
    right: {
      title: 'Flexible',
      description: 'Ad hoc, pratique, pragmatique',
    },
  },
  CAUTIOUSNESS: {
    imageUrl: courtImageUrl,
    left: {
      title: 'Prudent',
      description: 'Sûr, attentif, traditionnel, routinier',
    },
    right: {
      title: 'Impulsif',
      description: 'Expérimental, audacieux, risqué, informel',
    },
  },
  DUTIFULNESS: {
    imageUrl: courtImageUrl,
    left: {
      title: 'Dévoué',
      description: 'Accomplit les tâches avec diligence, suit les règles',
    },
    right: {
      title: 'Adaptable',
      description: 'Considère les devoirs de manière plus flexible et ouverte aux ajustements',
    },
  },
  ALTRUISM: {
    imageUrl: shuffleImageUrl,
    left: {
      title: 'Altruiste',
      description: 'Gentil, généreux, pour le bien commun',
    },
    right: {
      title: 'Réciproque',
      description: 'À chacun selon ses mérites, « un service pour un service », échange équitable',
    },
  },
  COOPERATION: {
    imageUrl: shuffleImageUrl,
    left: {
      title: 'Coopératif',
      description: 'Harmonieux, conciliant, accommodant',
    },
    right: {
      title: 'Compétitif',
      description: 'Têtu, moins disposé à faire des compromis',
    },
  },
  ETHICS: {
    imageUrl: shuffleImageUrl,
    left: {
      title: 'Franc',
      description: 'Naturel, sincère, ne voit pas la nécessité de faire semblant',
    },
    right: {
      title: 'Prudent',
      description: 'Soigné, poli, estime qu’un certain degré de simulation est nécessaire',
    },
  },
  ASSERTIVENESS: {
    imageUrl: peopleImageImageUrl,
    left: {
      title: 'Accommodant',
      description: 'Têtu, affirme clairement ses positions',
    },
    right: {
      title: 'Assertif',
      description: 'Têtu, affirme clairement ses positions',
    },
  },
  EMOTIONALITY: {
    imageUrl: compassImageUrl,
    left: {
      title: 'Compatissant',
      description: 'Montre de la sympathie pour les sentiments des autres, attentionné',
    },
    right: {
      title: 'Calme',
      description: 'Non affecté par les sentiments, indifférent',
    },
  },
  GROUNDEDNESS: {
    imageUrl: compassImageUrl,
    left: {
      title: 'Pratique',
      description: 'Tangible, concret, ancré, clair',
    },
    right: {
      title: 'Curieux intellectuellement',
      description: 'Conceptuel, complexe, aime débattre, pensée abstraite',
    },
  },
  IMAGINATION: {
    imageUrl: lightBulbImageUrl,
    left: {
      title: 'Restrictif',
      description: 'Méthodique, automatisé, moins créatif',
    },
    right: {
      title: 'Imaginatif',
      description: 'Créatif, ludique, orienté idées',
    },
  },
  FRIENDLINESS: {
    imageUrl: peopleImageImageUrl,
    left: {
      title: 'Sérieux',
      description: 'Sérieux, peut être perçu comme moins accessible',
    },
    right: {
      title: 'Amical',
      description: 'Accessible, facile à approcher',
    },
  },
  INDIVIDUALITY: {
    imageUrl: peopleImageImageUrl,
    left: {
      title: 'Orienté groupe',
      description: "Trouve plus facile de « s'intégrer » et d'adhérer au comportement du groupe",
    },
    right: {
      title: 'Individualiste',
      description: 'Autonome, indépendant, unique',
    },
  },
  SOCIABILITY: {
    imageUrl: peopleImageImageUrl,
    left: {
      title: 'Privé',
      description: 'Aime passer du temps seul, réservé',
    },
    right: {
      title: 'Sociable',
      description: 'Extraverti, bavard, apprécie l’ambiance sociale',
    },
  },
};
